import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Star, Mic, MicOff, Trash2, ChevronRight, ChevronLeft, Send,
  ExternalLink, CheckCircle, AlertCircle, Loader2, Upload, Check,
  Pencil,
} from 'lucide-react';
import { safeHtml, sanitizeHtml } from '@/lib/sanitize';
import { cleanQuestionLabel } from '@/lib/answerFormatting';
import { toast } from 'sonner';
import { computeQuestionNumbers } from '@/lib/questionNumbering';
import { NpsFace, resolveNpsPresentation } from '@/components/NpsFace';
import { FileDropzone } from '@/components/FileDropzone';
import { NpsFaces2026, StarRating2026, AttributeRatings2026, AccessCode2026 } from '@/components/standard2026/Standard2026';
import { TextVoice2026 } from '@/components/standard2026/TextVoice2026';
import { SurveyAccessGate } from '@/components/surveyAccess/SurveyAccessGate';
import { QRCodeSVG } from 'qrcode.react';
import { MobileOrEmail2026, ContactPreference2026, isValidMobileOrEmail, prefChoice, prefName } from '@/components/standard2026/ContactQuestions';
import '@/components/standard2026/standard2026.css';
import ChoiceFollowup from '@/components/ChoiceFollowup';
import { followupFor, isFollowupValid, isBlank, sameOption, selectedLabel } from '@/lib/choiceFollowups';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/726h775e_satisfind-notagline.png';
const DEFAULT_PRIMARY = '#1A3E6F';
const DEFAULT_SECONDARY = '#5BC4BF';

const COMMENTABLE_TYPES = new Set(['yes_no', 'button_choice', 'single_choice', 'multiple_choice', 'dropdown', 'star_rating', 'rating_scale']);

// Resolve a URL like "/api/surveys/builder/image/foo.png" to an absolute URL
// pointing at the backend (handles same-origin too — `API` already has /api).
const toAbsoluteUrl = (u) => {
  if (!u) return '';
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/api/')) {
    return API.replace(/\/api$/, '') + u;
  }
  return u;
};

// Country dial codes for the contact_info mobile field. Aligned with the
// curated list used by Branch Management plus a few extras the Pricing page
// targets. ISO code drives the IP-based default; phone is the +N dial code.
const COUNTRY_DIAL_CODES = [
  { iso: 'US', name: 'United States', phone: '+1' },
  { iso: 'CA', name: 'Canada', phone: '+1' },
  { iso: 'GB', name: 'United Kingdom', phone: '+44' },
  { iso: 'AU', name: 'Australia', phone: '+61' },
  { iso: 'PH', name: 'Philippines', phone: '+63' },
  { iso: 'SG', name: 'Singapore', phone: '+65' },
  { iso: 'MY', name: 'Malaysia', phone: '+60' },
  { iso: 'ID', name: 'Indonesia', phone: '+62' },
  { iso: 'IN', name: 'India', phone: '+91' },
  { iso: 'AE', name: 'United Arab Emirates', phone: '+971' },
  { iso: 'SA', name: 'Saudi Arabia', phone: '+966' },
  { iso: 'BH', name: 'Bahrain', phone: '+973' },
  { iso: 'QA', name: 'Qatar', phone: '+974' },
  { iso: 'KW', name: 'Kuwait', phone: '+965' },
  { iso: 'OM', name: 'Oman', phone: '+968' },
  { iso: 'JO', name: 'Jordan', phone: '+962' },
  { iso: 'LB', name: 'Lebanon', phone: '+961' },
  { iso: 'EG', name: 'Egypt', phone: '+20' },
  { iso: 'TR', name: 'Turkey', phone: '+90' },
  { iso: 'PK', name: 'Pakistan', phone: '+92' },
  { iso: 'HK', name: 'Hong Kong', phone: '+852' },
  { iso: 'JP', name: 'Japan', phone: '+81' },
  { iso: 'KR', name: 'South Korea', phone: '+82' },
  { iso: 'CN', name: 'China', phone: '+86' },
  { iso: 'FR', name: 'France', phone: '+33' },
  { iso: 'DE', name: 'Germany', phone: '+49' },
];
const DEFAULT_DIAL_CODE = '+1';

// Mobile field validation: 7–15 digits, numbers only.
const MOBILE_NUMBER_RE = /^\d{7,15}$/;
// Format-only email validation: name@domain.extension. Rejects missing @,
// missing domain/extension and any internal whitespace. Allows name+tag@sub.domain.com.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Resolve a sensible default dial code for the respondent based on their IP.
// Uses the public ipapi.co endpoint (no key required); on any failure we fall
// back to DEFAULT_DIAL_CODE so the form is never blocked.
async function fetchIpCountryDial() {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return null;
    const data = await res.json();
    const iso = (data?.country_code || '').toUpperCase();
    const match = COUNTRY_DIAL_CODES.find(c => c.iso === iso);
    return match ? match.phone : null;
  } catch {
    return null;
  }
}

// ─── Respondent NPS scale option (shared face set) ───
// Renders one score (0..10) in the configured display style + appearance.
// Interactions (per spec): hover / keyboard focus prominently enlarge the
// face WITHOUT shifting neighbours (CSS transform, origin bottom-centre); the
// selected option stays enlarged with a distinct brand ring; hover never
// selects — click / tap does. "Faces only" hides the visible number but keeps
// the exact value and an accessible score+classification label. CSS lives in
// index.css under `.nps-opt`.
const NpsScaleOption = ({ score, style, appearance, isSelected, onSelect, primaryColor }) => {
  const ring = primaryColor || '#1A3E6F';
  const showFace = style !== 'numbers';
  const showNumber = style !== 'faces';
  const a11y = `Score ${score}`;
  if (!showFace) {
    return (
      <button
        type="button"
        onClick={() => onSelect(score)}
        data-testid={`nps-score-${score}`}
        data-selected={isSelected ? 'true' : 'false'}
        aria-label={a11y}
        aria-pressed={isSelected}
        className="nps-opt inline-flex items-center justify-center focus:outline-none"
        style={{ width: '100%' }}
      >
        <span
          className="nps-visual inline-flex items-center justify-center rounded-lg font-semibold border"
          style={{
            aspectRatio: '1 / 1', width: '100%',
            borderColor: isSelected ? ring : '#E2E8F0',
            color: isSelected ? '#FFFFFF' : '#0F172A',
            background: isSelected ? ring : '#FFFFFF',
            boxShadow: isSelected ? '0 6px 16px -6px rgba(15,23,42,0.25)' : undefined,
            fontSize: 'clamp(12px, 2.4vw, 15px)',
          }}
        >
          {score}
        </span>
      </button>
    );
  }
  const faceSize = 'clamp(30px, 6.2vw, 54px)';
  return (
    <button
      type="button"
      onClick={() => onSelect(score)}
      data-testid={`nps-score-${score}`}
      data-selected={isSelected ? 'true' : 'false'}
      aria-label={a11y}
      aria-pressed={isSelected}
      className="nps-opt flex flex-col items-center gap-1 shrink-0 focus:outline-none bg-transparent"
    >
      <span
        className="nps-visual relative inline-flex items-center justify-center rounded-full"
        style={{
          width: faceSize, height: faceSize, borderRadius: '9999px',
          boxShadow: isSelected
            ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${ring}, 0 8px 18px -6px rgba(26,62,111,0.4)`
            : undefined,
        }}
      >
        <NpsFace score={score} appearance={appearance} size="100%" />
      </span>
      {showNumber && (
        <span
          className="font-semibold leading-none"
          style={{
            fontSize: 'clamp(10px, 1.8vw, 12px)',
            padding: isSelected ? '2px 7px' : '1px 2px',
            borderRadius: isSelected ? '9999px' : 0,
            background: isSelected ? ring : 'transparent',
            color: isSelected ? '#FFFFFF' : '#64748B',
          }}
        >
          {score}
        </span>
      )}
    </button>
  );
};

// Phase 2 — neutral emoji face for the Emoji Rating question type.
// Same "one colour, expression only" philosophy as NPSNeutralFace so
// the rating stays brand-agnostic. `score` = 1..total.
const EmojiRatingFace = ({ score, total, isSelected, onClick, label }) => {
  const t = total <= 1 ? 0 : ((score - 1) / (total - 1)) * 2 - 1; // [-1..1]
  const cy = 68 + (t * 14);
  const my = 68;
  const stroke = '#475569';
  const fill = '#F1F5F9';
  const ring = '#1A3E6F';
  const mouth = `M32 ${my} C 44 ${cy}, 56 ${cy}, 68 ${my}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center transition-all focus:outline-none"
      data-testid={`emoji-face-${score}`}
      aria-label={label || `Rating ${score} of ${total}`}
      aria-pressed={isSelected}
      style={{ transform: isSelected ? 'translateY(-3px)' : undefined }}
    >
      <span
        className="relative inline-flex items-center justify-center rounded-full"
        style={{
          width: 'clamp(44px, 12vw, 72px)',
          height: 'clamp(44px, 12vw, 72px)',
          boxShadow: isSelected
            ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${ring}, 0 6px 16px -4px rgba(26,62,111,0.35)`
            : undefined,
        }}
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden>
          <circle cx="50" cy="50" r="46" fill={fill} stroke={stroke} strokeWidth="3" />
          <circle cx="36" cy="42" r="5" fill={stroke} />
          <circle cx="64" cy="42" r="5" fill={stroke} />
          <path d={mouth} stroke={stroke} strokeWidth="4" strokeLinecap="round" fill="none" />
        </svg>
      </span>
      {label ? (
        <span className="mt-1 text-[10px] sm:text-xs text-[#64748B] font-medium leading-tight text-center px-0.5">{label}</span>
      ) : null}
    </button>
  );
};

// Phase 2 — Ranking list. Two interactions:
//   • Native HTML5 drag-and-drop for desktop / trackpad.
//   • Explicit ↑ / ↓ buttons for touch devices (mobile fallback).
// Emits `onReorder(ids)` with the freshly-ordered array of option IDs
// after every change so the parent can persist immediately.
function RankingList({ orderedOpts, questionId, onReorder, onMoveUp, onMoveDown }) {
  const [dragIndex, setDragIndex] = React.useState(null);
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const total = orderedOpts.length;
  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (i, e) => { e.preventDefault(); if (i !== hoverIndex) setHoverIndex(i); };
  const onDrop = (i, e) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === i) { setDragIndex(null); setHoverIndex(null); return; }
    const ids = orderedOpts.map(o => o.id);
    const [moved] = ids.splice(dragIndex, 1);
    ids.splice(i, 0, moved);
    onReorder(ids);
    setDragIndex(null); setHoverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setHoverIndex(null); };
  return (
    <div className="space-y-2" data-testid={`ranking-${questionId}`}>
      {orderedOpts.map((o, i) => (
        <div
          key={o.id}
          draggable
          onDragStart={() => onDragStart(i)}
          onDragOver={(e) => onDragOver(i, e)}
          onDrop={(e) => onDrop(i, e)}
          onDragEnd={onDragEnd}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white cursor-grab active:cursor-grabbing select-none"
          style={{
            borderColor: hoverIndex === i && dragIndex !== null && dragIndex !== i ? '#1A3E6F' : '#E2E8F0',
            opacity: dragIndex === i ? 0.55 : 1,
          }}
          data-testid={`ranking-item-${questionId}-${i}`}
          data-option-id={o.id}
        >
          <span className="text-[11px] font-bold text-[#94A3B8] w-5 text-center">{i + 1}</span>
          <span className="flex-1 text-sm text-[#0F172A]">{o.label}</span>
          <div className="flex flex-col gap-1">
            <button type="button" onClick={() => onMoveUp(i)} disabled={i === 0} className="text-xs px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[#64748B] disabled:opacity-30" data-testid={`ranking-up-${questionId}-${i}`} aria-label="Move up">↑</button>
            <button type="button" onClick={() => onMoveDown(i)} disabled={i === total - 1} className="text-xs px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[#64748B] disabled:opacity-30" data-testid={`ranking-down-${questionId}-${i}`} aria-label="Move down">↓</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Voice Recorder (lightweight) ───
function VoiceRecorder({ questionId, maxSeconds = 120, onResult, onBusyChange, lockRecord = false, compact = false, autoStart = false, onRemove, initial = null }) {
  // states: idle | permission-pending | recording | success | error
  // NOTE: the respondent flow NEVER transcribes in the browser. Once the audio
  // recording is captured we go straight to `success` and preserve the blob on
  // onResult; transcription happens server-side after submission (for
  // client/admin viewing only) and can never block or error the respondent.
  const [state, setState] = useState(initial ? 'success' : 'idle');
  const [seconds, setSeconds] = useState(0);
  const [playbackUrl, setPlaybackUrl] = useState(() => (initial && onRemove ? URL.createObjectURL(initial.blob) : ''));
  const autoStartedRef = useRef(false);
  const startedAtRef = useRef(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  // Holds the most recent recording so the user can retry transcription on the
  // already-captured audio without having to record again.
  const lastRecordingRef = useRef(null); // { blob, duration }

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRef.current && mediaRef.current.state !== 'inactive') {
      try { mediaRef.current.stop(); } catch {}
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);
  // Block the parent Next button only while we're acquiring the mic or actively
  // recording. Once the recording is saved (`success`) the answer is final.
  useEffect(() => {
    onBusyChange?.(
      state === 'permission-pending' || state === 'recording'
    );
  }, [state, onBusyChange]);

  const start = async () => {
    // Don't let the respondent start recording while the question's audio
    // prompt is still playing (prevents the mic capturing the prompt audio).
    if (lockRecord) return;
    setState('permission-pending');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const capturedSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        // Save the recording and finish immediately — no in-browser
        // transcription. The blob is preserved on the answer and uploaded on
        // submit; the backend transcribes it afterwards for client/admin use.
        lastRecordingRef.current = { blob, duration: capturedSeconds };
        if (onRemove) setPlaybackUrl(URL.createObjectURL(blob));
        onResult?.({ duration: capturedSeconds, blob });
        setState('success');
      };
      mr.start();
      startedAtRef.current = Date.now();
      setState('recording');
      setSeconds(0);
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSeconds(elapsed);
        if (elapsed >= maxSeconds) stop();
      }, 1000);
    } catch {
      toast.error('Microphone access denied. Please type your answer instead.');
      setState('error');
    }
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
  };

  const reset = () => { lastRecordingRef.current = null; setState('idle'); setSeconds(0); };

  useEffect(() => () => { if (playbackUrl) URL.revokeObjectURL(playbackUrl); }, [playbackUrl]);
  // Text-first "Switch to Voice" confirm starts recording straight away (guarded for StrictMode).
  useEffect(() => {
    if (autoStart && !autoStartedRef.current) { autoStartedRef.current = true; start(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return (
    <div className={`flex flex-col ${compact ? 'items-start' : 'items-center'} gap-3`} data-testid={`voice-recorder-${questionId}`}>
      {state === 'success' && onRemove && (
        <div className="w-full flex flex-col gap-2" data-testid="voice-recorded-confirmation">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
            <Check className="w-4 h-4 text-[#16A34A]" /> Voice response recorded
          </span>
          {playbackUrl && <audio controls src={playbackUrl} preload="metadata" className="w-full" data-testid="voice-playback" />}
          <button type="button" onClick={onRemove} className="self-start inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-sm font-medium text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors" data-testid="voice-remove-btn">
            <Trash2 className="w-4 h-4" /> Remove recording
          </button>
        </div>
      )}
      {state === 'idle' && compact && (
        <button
          type="button"
          onClick={start}
          disabled={lockRecord}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#EEF2F7] hover:bg-[#E2E8F0] text-sm font-semibold text-[#334155] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="voice-start-btn"
        >
          <Mic className="w-4 h-4" /> Record Voice
        </button>
      )}
      {state === 'idle' && !compact && (
        <>
          <button
            onClick={start}
            disabled={lockRecord}
            className={`w-16 h-16 rounded-full flex items-center justify-center transition ${lockRecord ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ backgroundColor: '#EF4444' }}
            data-testid="voice-start-btn"
          >
            <Mic className="w-8 h-8 text-white" />
          </button>
          <span className="text-sm text-[#64748B] text-center" data-testid="voice-idle-instruction">
            {lockRecord ? 'Please finish listening to the question before recording' : 'Tap the microphone to start recording'}
          </span>
        </>
      )}
      {state === 'permission-pending' && (
        <>
          <button disabled className="w-16 h-16 rounded-full flex items-center justify-center cursor-not-allowed" style={{ backgroundColor: '#F87171' }} data-testid="voice-permission-pending-btn">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </button>
          <span className="text-sm text-[#64748B] text-center" data-testid="voice-permission-text">
            Waiting for microphone access…
          </span>
        </>
      )}
      {state === 'recording' && (
        <>
          <div className="relative flex items-center justify-center">
            {/* Brand-navy expanding ring — visual cue that recording is active.
                Sits behind the mic button so the button colour stays unchanged. */}
            <span className="absolute inline-flex h-16 w-16 rounded-full opacity-75 animate-ping" style={{ backgroundColor: '#1A3E6F' }} />
            <button onClick={stop} className="relative w-16 h-16 rounded-full flex items-center justify-center bg-[#EF4444]" data-testid="voice-stop-btn">
              <MicOff className="w-8 h-8 text-white" />
            </button>
          </div>
          <span className="text-xs text-[#64748B]" data-testid="voice-recording-indicator">Recording… {seconds}s / {maxSeconds}s</span>
        </>
      )}
      {state === 'success' && !onRemove && (
        <div className="flex flex-col items-center gap-2" data-testid="voice-recorded-confirmation">
          <div className="w-12 h-12 rounded-full bg-[#22C55E] flex items-center justify-center">
            <Check className="w-7 h-7 text-white" />
          </div>
          <span className="text-sm font-medium text-[#0F172A]">Response recorded</span>
          <button onClick={reset} className="text-xs text-[#64748B] mt-1 flex items-center gap-1 hover:underline" data-testid="voice-rerecord-btn">
            <Trash2 className="w-3 h-3" /> Re-record
          </button>
        </div>
      )}
      {state === 'error' && (
        <button onClick={reset} className="text-sm text-red-600 flex items-center gap-1" data-testid="voice-error-retry-btn">
          <AlertCircle className="w-4 h-4" /> Try again
        </button>
      )}
    </div>
  );
}

// ─── Question Renderer ───
const VOICE_MODE_STORAGE_KEY = 'vox.voice_mode_pref';

function VoiceOrTypeAnswer({ question, answer, primaryColor, onChange, onVoiceBusyChange, sessionKey, lockRecord = false }) {
  const vs = question.voice_settings || { max_duration_seconds: 120 };

  // Pick the starting mode: user's remembered choice for this submission wins;
  // otherwise text-first when the question opts in (standard_2026 reason),
  // else default to 'voice' so the mic is one tap away (legacy behaviour).
  const defaultMode = question.response_mode === 'text_first' ? 'text' : 'voice';
  const initialMode = (() => {
    if (typeof window === 'undefined') return defaultMode;
    try {
      const saved = window.sessionStorage.getItem(`${VOICE_MODE_STORAGE_KEY}:${sessionKey || ''}`);
      if (saved === 'voice' || saved === 'text') return saved;
    } catch { /* ignore */ }
    return defaultMode;
  })();
  const [mode, setMode] = useState(initialMode);
  const [typedText, setTypedText] = useState('');

  const rememberMode = (m) => {
    try {
      window.sessionStorage.setItem(`${VOICE_MODE_STORAGE_KEY}:${sessionKey || ''}`, m);
    } catch { /* ignore */ }
  };

  const switchToText = () => {
    setMode('text');
    rememberMode('text');
    onChange(undefined); // clear any prior voice answer (audio + transcript)
  };

  const switchToVoice = () => {
    setMode('voice');
    rememberMode('voice');
    setTypedText('');
    onChange(undefined); // clear any typed text from the answer payload
  };

  const handleTyped = (val) => {
    setTypedText(val);
    onChange(val ? { text: val } : undefined);
  };

  const helperRaw = typeof question.voice_helper_text === 'string' ? question.voice_helper_text.trim() : '';

  return (
    <div data-testid={`voice-or-type-${question.id}`}>
      {helperRaw && question.response_mode !== 'text_first' && (
        <p className="text-[11px] text-[#64748B] italic mb-3" data-testid={`voice-helper-text-${question.id}`}>
          {helperRaw}
        </p>
      )}

      {question.response_mode === 'text_first' ? (
        /* standard_2026: typed text is primary; voice is an optional,
           mutually-exclusive alternative (no tabs). */
        <TextVoice2026
          question={question}
          answer={answer}
          onChange={onChange}
          onVoiceBusyChange={onVoiceBusyChange}
          renderRecorder={({ key, ...p }) => (
            <VoiceRecorder key={key} questionId={question.id} maxSeconds={vs.max_duration_seconds} lockRecord={lockRecord} compact {...p} />
          )}
        />
      ) : (
      <>
      {/* Mode tabs — equal weight, one tap to switch. Record on the left keeps
          the voice flow visually primary without burying Type as a tiny link. */}
      <div className="grid grid-cols-2 gap-1 bg-[#F1F5F9] p-1 rounded-xl mb-4" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'voice'}
          onClick={switchToVoice}
          className={`h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === 'voice' ? 'bg-[#1A3E6F] text-white shadow-sm' : 'bg-transparent text-[#475569] hover:text-[#0F172A]'}`}
          data-testid={`voice-tab-record-${question.id}`}
        >
          <Mic className="w-4 h-4" /> Record
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'text'}
          onClick={switchToText}
          className={`h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === 'text' ? 'bg-[#1A3E6F] text-white shadow-sm' : 'bg-transparent text-[#475569] hover:text-[#0F172A]'}`}
          data-testid={`voice-tab-type-${question.id}`}
        >
          <Pencil className="w-4 h-4" /> Type
        </button>
      </div>

      {mode === 'voice' ? (
        <VoiceRecorder
          questionId={question.id}
          maxSeconds={vs.max_duration_seconds}
          onResult={(r) => onChange(r)}
          onBusyChange={onVoiceBusyChange}
          lockRecord={lockRecord}
        />
      ) : (
        <Textarea
          rows={4}
          value={typedText}
          onChange={(e) => handleTyped(e.target.value)}
          placeholder={question.placeholder_text || 'Type your answer here…'}
          data-testid={`voice-text-fallback-${question.id}`}
          style={{ borderColor: '#E2E8F0' }}
        />
      )}
      </>
      )}
    </div>
  );
}

/* =============== Matrix question — public form rendering =============
 * Schema:
 *   {
 *     type: 'matrix',
 *     items_source: 'fixed' | 'freetext',
 *     items: [...],
 *     criteria: [...],
 *     scale: 'excellent_poor'|'always_never'|'agree_disagree'|'stars'|'csat_4pt'|'expectations_3pt'|'likelihood_4pt'|'yes_no'|'custom',
 *     custom_scale: [...]   // when scale === 'custom'
 *   }
 *
 * Answer shape:
 *   { type: 'matrix', items_source, items: [
 *       { item: 'Chicken Katsu Don', Taste: 'Excellent', Freshness: 'Good' },
 *       ...
 *     ]
 *   }
 */
const _MATRIX_PRESETS = {
  excellent_poor: ['Excellent', 'Good', 'Average', 'Poor'],
  always_never:   ['Always', 'Usually', 'Sometimes', 'Never'],
  agree_disagree: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
  stars:          ['5 Stars', '4 Stars', '3 Stars', '2 Stars', '1 Star'],
  csat_4pt:         ['Very Satisfied', 'Satisfied', 'Unsatisfied', 'Very Unsatisfied'],
  expectations_3pt: ['Exceeded expectations', 'Met expectations', 'Below expectations'],
  likelihood_4pt:   ['Very Likely', 'Likely', 'Unlikely', 'Very Unlikely'],
  yes_no:           ['Yes', 'No'],
};

function getMatrixScaleLabels(question) {
  const scale = question?.scale || 'excellent_poor';
  if (scale === 'custom') {
    const c = Array.isArray(question?.custom_scale) ? question.custom_scale.filter(Boolean) : [];
    return c.length >= 2 ? c : ['Excellent', 'Good', 'Average', 'Poor'];
  }
  return _MATRIX_PRESETS[scale] || _MATRIX_PRESETS.excellent_poor;
}

function MatrixQuestion({ question, answer, onChange, primaryColor, questionNumber }) {
  const itemsSource = question.items_source || 'fixed';
  const fixedItems = (Array.isArray(question.items) ? question.items : []).filter(Boolean);
  const criteria = (Array.isArray(question.criteria) ? question.criteria : []).filter(Boolean);
  const scaleLabels = getMatrixScaleLabels(question);

  // Single-axis matrix: a fixed-list matrix with NO items but criteria defined
  // (e.g. "How important are the following…"). Render the criteria directly
  // against the scale using one implicit, unlabelled row.
  const singleAxisFixed = itemsSource === 'fixed' && fixedItems.length === 0 && criteria.length > 0;
  const effectiveFixedItems = singleAxisFixed ? [''] : fixedItems;

  // Free-text mode keeps the typed input + a generated list of items.
  const [rawText, setRawText] = React.useState('');
  // Active items (either fixed or generated from text)
  const initialActiveItems = itemsSource === 'freetext'
    ? (Array.isArray(answer?.items) ? answer.items.map(r => r.item) : [])
    : effectiveFixedItems;
  const [activeItems, setActiveItems] = React.useState(initialActiveItems);

  // The structured answer rows that get bubbled up via onChange.
  const rows = Array.isArray(answer?.items) ? answer.items : [];
  const getRow = (item) => rows.find(r => r.item === item) || { item };

  const setRating = (item, criterion, value) => {
    const existingIdx = rows.findIndex(r => r.item === item);
    const next = [...rows];
    if (existingIdx >= 0) {
      next[existingIdx] = { ...next[existingIdx], [criterion]: value };
    } else {
      next.push({ item, [criterion]: value });
    }
    onChange({
      type: 'matrix',
      items_source: itemsSource,
      items: next,
    });
  };

  const generate = () => {
    const items = rawText
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(Boolean);
    if (items.length === 0) return;
    setActiveItems(items);
    // Seed empty answer rows so existing answer object stays in sync.
    onChange({
      type: 'matrix',
      items_source: itemsSource,
      items: items.map(it => {
        const existing = rows.find(r => r.item === it);
        return existing || { item: it };
      }),
    });
  };

  const titleHtml = (questionNumber ? `<span class="text-[#64748B] font-semibold mr-1.5">${questionNumber}.</span>` : '')
    + (question.text || '')
    + (question.is_required ? ' <span class="text-red-500">*</span>' : '');

  return (
    <div className="space-y-3" data-testid={`matrix-${question.id}`}>
      <h2 className="text-base font-semibold text-[#0F172A]" dangerouslySetInnerHTML={safeHtml(titleHtml)} />

      {itemsSource === 'freetext' && activeItems.length === 0 && (
        <div className="space-y-2">
          <Textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Enter the items you'd like to rate (one per line or comma separated)"
            rows={4}
            data-testid={`matrix-freetext-${question.id}`}
          />
          <Button
            onClick={generate}
            disabled={!rawText.trim()}
            style={{ backgroundColor: primaryColor }}
            className="text-white"
            data-testid={`matrix-generate-${question.id}`}
          >
            Generate ratings →
          </Button>
        </div>
      )}

      {activeItems.length > 0 && (
        <div className="space-y-3">
          {activeItems.map((item, idx) => (
            <div key={`${item}-${idx}`} className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid={`matrix-item-card-${idx}`}>
              {item ? <p className="font-semibold text-sm text-[#0F172A] mb-2">{item}</p> : null}
              <div className="space-y-2">
                {criteria.map((c, ci) => {
                  const selected = getRow(item)[c];
                  return (
                    <div key={ci} className="space-y-1.5">
                      <p className="text-xs text-[#64748B]">{c}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {scaleLabels.map((label, li) => {
                          const active = selected === label;
                          return (
                            <button
                              key={li}
                              type="button"
                              onClick={() => setRating(item, c, label)}
                              className={`px-2.5 py-1.5 rounded-full border text-xs transition-all ${active ? 'border-transparent text-white font-medium' : 'border-[#E2E8F0] text-[#334155] hover:border-[#1A3E6F]'}`}
                              style={active ? { backgroundColor: primaryColor } : {}}
                              data-testid={`matrix-rate-${idx}-${ci}-${li}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {itemsSource === 'freetext' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-[#64748B]"
              onClick={() => { setActiveItems([]); setRawText(''); onChange(null); }}
              data-testid={`matrix-reset-${question.id}`}
            >
              ← Edit items
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


// Fix 4 — Public form file upload editor (multi-file, progress, video, retry).
// Uses local React state for per-file pending/error indicators so we don't
// have to thread async uploads through the parent answers map. The committed
// answer (passed up via onChange) only contains "real" files — pending and
// failed uploads stay local until they succeed.
function FileUploadQuestion({ question, answer, onChange, branchId, onUploadingChange, isPreview = false }) {
  const fs = question.file_settings || {};
  const allowed = Array.isArray(fs.allowed_types) && fs.allowed_types.length
    ? fs.allowed_types
    : (fs.accepted === 'pdf' ? ['pdf'] : fs.accepted === 'any' ? ['image', 'pdf'] : ['image']);
  const maxFiles = Math.max(1, Math.min(10, Number(fs.max_files) || 1));
  const maxSizeMb = [1, 2, 5, 10, 20, 50].includes(Number(fs.max_file_size_mb))
    ? Number(fs.max_file_size_mb)
    : ([1, 2, 5, 10, 20, 50].includes(Number(fs.max_size_mb)) ? Number(fs.max_size_mb) : 5);

  const acceptParts = [
    allowed.includes('image') ? 'image/jpeg,image/png,image/webp' : '',
    allowed.includes('pdf') ? 'application/pdf' : '',
    allowed.includes('video') ? 'video/mp4,video/quicktime,video/webm' : '',
  ].filter(Boolean);
  const acceptAttr = acceptParts.length ? acceptParts.join(',') : 'image/*';

  const allowedMimes = new Set([
    ...(allowed.includes('image') ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] : []),
    ...(allowed.includes('pdf') ? ['application/pdf'] : []),
    ...(allowed.includes('video') ? ['video/mp4', 'video/quicktime', 'video/x-quicktime', 'video/webm'] : []),
  ]);

  const typeLabel = (() => {
    const parts = [];
    if (allowed.includes('image')) parts.push('image');
    if (allowed.includes('pdf')) parts.push('pdf');
    if (allowed.includes('video')) parts.push('video');
    return parts.join(' / ') || 'image';
  })();

  // Committed (parent) files
  const committed = Array.isArray(answer) ? answer : [];
  // Local "in-flight" entries (pending + failed). Each has a unique _key.
  const [transient, setTransient] = useState([]);
  // Live mirror of the latest committed answer so sequential uploads (and
  // comment edits made mid-batch) are never clobbered by a stale closure.
  const answerRef = useRef(answer);
  answerRef.current = answer;

  // Combined list for render
  const all = [...committed, ...transient];
  const anyUploading = transient.some(t => t._pending);
  const pendingCount = transient.filter(t => t._pending).length;
  // Existing committed + in-progress uploads both count toward the limit.
  // Failed transients do NOT (they hold a visible retry/remove tile only).
  const activeCount = committed.length + pendingCount;
  const reachedLimit = activeCount >= maxFiles;
  const remainingSlots = Math.max(0, maxFiles - activeCount);
  const successCount = committed.length;
  const totalCount = all.length;
  const allowMultiplePick = maxFiles > 1;

  useEffect(() => { onUploadingChange && onUploadingChange(question.id, anyUploading); }, [anyUploading, onUploadingChange, question.id]);

  const isVideo = (f) => {
    const t = (f && f.type) || '';
    if (t.startsWith('video/')) return true;
    const u = (f && f.url) || (f && f.name) || '';
    return /\.(mp4|mov|webm)$/i.test(u);
  };
  const isImg = (f) => {
    const t = (f && f.type) || '';
    if (t.startsWith('image/')) return true;
    const u = (f && f.url) || '';
    return /\.(jpe?g|png|webp|gif)$/i.test(u);
  };

  const doUpload = async (file) => {
    // Builder preview: keep the file local, never store it.
    if (isPreview) return { url: URL.createObjectURL(file), comment: '', name: file.name, type: file.type, size: file.size };
    const form = new FormData();
    form.append('file', file);
    form.append('branch_id', branchId || '');
    form.append('question_id', question.id);
    form.append('allowed_types', allowed.join(','));
    form.append('max_file_size_mb', String(maxSizeMb));
    const res = await fetch(`${API}/public/survey/upload-file`, { method: 'POST', body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    const data = await res.json();
    return { url: data.file_url, comment: '', name: file.name, type: file.type, size: file.size };
  };

  const onPickMulti = async (files) => {
    if (!files.length) return;
    // Reject the WHOLE batch if it would exceed the remaining allowance —
    // never silently drop the overflow.
    if (files.length > remainingSlots) {
      toast.error(`You can upload ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'}.`);
      return;
    }
    // Upload sequentially; each commit reads the LATEST answer via answerRef
    // so earlier files (and any comments typed meanwhile) are preserved.
    for (const file of files) {
      if (!allowedMimes.has((file.type || '').toLowerCase())) {
        toast.error(`${file.name}: file type not supported (${typeLabel} only)`);
        continue;
      }
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name}: exceeds ${maxSizeMb}MB limit`);
        continue;
      }
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setTransient(prev => [...prev, { _key: key, _pending: true, _file: file, name: file.name, size: file.size, type: file.type }]);
      try {
        const entry = await doUpload(file);
        // Move to committed
        setTransient(prev => prev.filter(t => t._key !== key));
        onChange([...(Array.isArray(answerRef.current) ? answerRef.current : []), entry]);
      } catch (e) {
        setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: e.message || 'Upload failed' } : t));
      }
    }
  };

  const retryTransient = async (key) => {
    const target = transient.find(t => t._key === key);
    if (!target) return;
    setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: true, _error: undefined } : t));
    try {
      const entry = await doUpload(target._file);
      setTransient(prev => prev.filter(t => t._key !== key));
      onChange([...(Array.isArray(answerRef.current) ? answerRef.current : []), entry]);
    } catch (e) {
      setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: e.message || 'Upload failed' } : t));
    }
  };

  const removeTransient = (key) => setTransient(prev => prev.filter(t => t._key !== key));
  const removeCommittedAt = (idx) => {
    const next = committed.filter((_, i) => i !== idx);
    onChange(next.length ? next : undefined);
  };
  const updateCommentAt = (idx, comment) => {
    const next = committed.map((f, i) => i === idx ? { ...f, comment } : f);
    onChange(next);
  };

  return (
    <div>
      {totalCount > 0 && (
        <div className="mb-2 text-xs font-semibold text-[#1A3E6F]" data-testid={`file-count-${question.id}`}>
          {anyUploading
            ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading… please wait</span>
            : <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-600" /> {successCount} file{successCount === 1 ? '' : 's'} uploaded</span>
          }
        </div>
      )}
      <div className="space-y-3" data-testid={`file-upload-${question.id}`}>
        {committed.map((f, i) => (
          <div key={`c-${i}`} className="border rounded-lg p-2.5 border-[#E2E8F0] bg-[#F8FAFC]" data-testid={`file-row-${question.id}-${i}`}>
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-[88px] h-[88px] rounded bg-white border border-[#E2E8F0] flex items-center justify-center overflow-hidden">
                {isImg(f) && f.url ? (
                  <a href={f.url} target="_blank" rel="noreferrer" className="w-full h-full block">
                    <img src={f.url} alt={f.name || ''} className="w-full h-full object-cover" />
                  </a>
                ) : isVideo(f) ? (
                  <span className="text-[10px] text-[#64748B] font-semibold uppercase">Video</span>
                ) : (
                  <Upload className="w-5 h-5 text-[#64748B]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" data-testid={`file-tick-${question.id}-${i}`} />
                  <span className="text-xs font-medium text-[#0F172A] truncate" title={f.name}>{f.name || 'file'}</span>
                </div>
                {f.size != null && <p className="text-[10px] text-[#94A3B8]">{(f.size / 1024 / 1024).toFixed(2)} MB</p>}
                <Input
                  value={f.comment || ''}
                  onChange={(e) => updateCommentAt(i, e.target.value)}
                  placeholder="Add a comment about this file..."
                  className="text-xs h-7 mt-1"
                  data-testid={`file-comment-${question.id}-${i}`}
                />
              </div>
              <button type="button" onClick={() => removeCommittedAt(i)} aria-label="Remove file" className="text-[#D62B27] hover:bg-rose-100 rounded p-1 shrink-0" data-testid={`file-remove-${question.id}-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {transient.map((t) => (
          <div key={t._key} className={`border rounded-lg p-2.5 ${t._error ? 'border-rose-300 bg-rose-50' : 'border-blue-200 bg-blue-50'}`} data-testid={`file-transient-${question.id}-${t._key}`}>
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-[88px] h-[88px] rounded bg-white border border-[#E2E8F0] flex items-center justify-center">
                {t._pending ? <Loader2 className="w-5 h-5 animate-spin text-[#1A3E6F]" /> : <AlertCircle className="w-5 h-5 text-rose-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#0F172A] truncate" title={t.name}>{t.name}</p>
                {t.size != null && <p className="text-[10px] text-[#94A3B8]">{(t.size / 1024 / 1024).toFixed(2)} MB</p>}
                {t._error && (
                  <div className="mt-1 space-y-1">
                    <p className="text-[11px] text-rose-700">Upload failed. Please try again.</p>
                    <button type="button" onClick={() => retryTransient(t._key)} className="text-[11px] text-[#1A3E6F] underline font-semibold" data-testid={`file-retry-${question.id}-${t._key}`}>Retry</button>
                  </div>
                )}
                {t._pending && <p className="text-[11px] text-[#1A3E6F] mt-1">Uploading…</p>}
              </div>
              <button type="button" onClick={() => removeTransient(t._key)} aria-label="Remove" className="text-[#D62B27] hover:bg-rose-100 rounded p-1 shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {reachedLimit ? (
          <p className="text-xs text-[#64748B] italic" data-testid={`file-limit-${question.id}`}>Maximum {maxFiles} file{maxFiles === 1 ? '' : 's'} uploaded</p>
        ) : (
          <FileDropzone
            onFiles={onPickMulti}
            accept={acceptAttr}
            multiple={allowMultiplePick}
            disabled={anyUploading}
            idleLabel={`${totalCount === 0 ? `Upload ${typeLabel}` : 'Add another'} (${maxSizeMb}MB max)`}
            subLabel={maxFiles > 1 ? `— ${activeCount}/${maxFiles} so far` : ''}
            inputTestId={`file-input-${question.id}`}
            dropTestId={`file-dropzone-${question.id}`}
          />
        )}
      </div>
    </div>
  );
}


function QuestionRenderer({ question, answer, onChange, primaryColor, privacyUrl, termsUrl, brandName, onVoiceBusyChange, step, showConsent, defaultDialCode, sessionKey, branchId, onAdvance, questionNumber, onUploadingChange, presentation = 'legacy_v1', isPreview = false, followupValues = {}, onFollowupChange = () => {} }) {
  const isStd2026 = presentation === 'standard_2026';
  const renderPlaceholder = question.placeholder_text || '';
  const required = !!question.is_required;
  // True while the question's audio prompt is actively playing — used to block
  // starting a voice recording until the respondent has finished listening.
  const [promptAudioPlaying, setPromptAudioPlaying] = useState(false);
  const audioPlayHandlers = {
    onPlay: () => setPromptAudioPlaying(true),
    onPause: () => setPromptAudioPlaying(false),
    onEnded: () => setPromptAudioPlaying(false),
  };

  // Phase 4A: "[Brand Name]" in wording resolves to the client's brand name.
  const withBrand = (html) => String(html || '').replace(/\[brand name\]/gi, brandName || '');

  const renderGuidance = () => (question.guidance_note ? (
    <div className="text-xs text-[#64748B] mt-1" dangerouslySetInnerHTML={safeHtml(withBrand(question.guidance_note))} />
  ) : null);

  // Phase 1: optional stimulus image + audio/video prompts. Rendered
  // above every real question via `renderLabel()`. Absent = no-op
  // (existing questions render exactly as before).
  const renderMedia = () => {
    const imgU = question.question_image_url ? toAbsoluteUrl(question.question_image_url) : '';
    const audU = question.audio_prompt_url ? toAbsoluteUrl(question.audio_prompt_url) : '';
    const vidU = question.video_prompt_url ? toAbsoluteUrl(question.video_prompt_url) : '';
    if (!imgU && !audU && !vidU) return null;
    return (
      <div className="space-y-2 mb-3" data-testid={`q-media-${question.id}`}>
        {imgU && (
          <img
            src={imgU}
            alt=""
            className="w-full max-h-64 object-contain rounded-lg border border-[#E2E8F0]"
            data-testid={`q-image-${question.id}`}
          />
        )}
        {audU && (
          <audio src={audU} controls preload="metadata" className="w-full" data-testid={`q-audio-${question.id}`} {...audioPlayHandlers} />
        )}
        {vidU && (
          <video
            src={vidU}
            controls
            playsInline
            className="w-full rounded-lg border border-[#E2E8F0]"
            data-testid={`q-video-${question.id}`}
          />
        )}
      </div>
    );
  };

  const numberPrefix = questionNumber ? `<span class="text-[#64748B] font-semibold mr-1.5" data-testid="q-number-${question.id}">${questionNumber}.</span>` : '';

  const renderLabel = () => (
    <>
      {renderMedia()}
      <div className="mb-2">
        <h2 className="text-base font-semibold text-[#0F172A]" data-testid={`q-text-${question.id}`} dangerouslySetInnerHTML={{ __html: numberPrefix + withBrand(sanitizeHtml(question.text)) + (required ? ' <span class="text-red-500">*</span>' : '') }} />
        {renderGuidance()}
      </div>
    </>
  );

  switch (question.type) {
    case 'attribute_ratings':
      return (
        <div>
          {renderLabel()}
          <AttributeRatings2026 question={question} answer={answer} onChange={onChange} />
        </div>
      );
    case 'mobile_or_email':
      return (
        <div>
          {renderLabel()}
          <MobileOrEmail2026 question={question} answer={answer} onChange={onChange} required={required} />
        </div>
      );
    case 'contact_preference':
      return (
        <div>
          {renderLabel()}
          <ContactPreference2026 question={question} answer={answer} onChange={onChange} primaryColor={primaryColor} />
        </div>
      );
    case 'survey_access_code':
      return (
        <div>
          {renderLabel()}
          <AccessCode2026 question={question} answer={answer} onChange={onChange} primaryColor={primaryColor} />
        </div>
      );
    case 'welcome': {
      // B1: Welcome Screen — image (above title) + title + subtitle + Start button.
      const ws = question.welcome_settings || {};
      const title = ws.title || question.text || 'Welcome';
      const subtitle = ws.subtitle || question.sub_text || '';
      const buttonLabel = ws.button_label || 'Start';
      const imgUrl = question.welcome_image_url ? toAbsoluteUrl(question.welcome_image_url) : '';
      // standard_2026 presents the survey's reusable consent (survey-level
      // `show_consent`) HERE on the Welcome screen as a Data Privacy section
      // with a required checkbox that gates Continue. The consent response is
      // stored via the existing mechanism (`consent_given` on this screen's
      // answer) — no separate consent system. legacy_v1 welcome screens never
      // collect consent (it stays on the contact screen as before).
      const consentCfg = ws.consent || {};
      const consentActive = isStd2026 && showConsent;
      const consentGiven = !!(answer && answer.consent_given);
      return (
        <div className="text-center" data-testid={`welcome-${question.id}`}>
          {imgUrl && (
            <img src={imgUrl} alt="" className="w-full max-h-56 object-cover rounded-lg mb-4" data-testid={`welcome-image-${question.id}`} />
          )}
          <h1 className="text-xl font-bold text-[#0F172A] mb-2" dangerouslySetInnerHTML={safeHtml(title)} />
          {subtitle && <p className="text-sm text-[#475569] mb-5 whitespace-pre-wrap">{subtitle}</p>}
          {consentActive && (
            <div className="text-left mb-5" data-testid={`welcome-consent-${question.id}`}>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-[#0F172A] mb-1">{consentCfg.heading || 'Data Privacy'}</p>
                {consentCfg.notice && <p className="text-xs text-[#475569] whitespace-pre-wrap">{consentCfg.notice}</p>}
              </div>
              <label className="flex items-start gap-2 text-xs text-[#475569] cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={consentGiven}
                  onChange={(e) => onChange && onChange({ ...(answer || {}), consent_given: e.target.checked })}
                  data-testid={`welcome-consent-checkbox-${question.id}`}
                />
                <span>
                  I agree to provide feedback about {brandName || 'this business'}. My personal information will be handled in accordance with their{' '}
                  {privacyUrl ? <a href={privacyUrl} target="_blank" rel="noreferrer" className="underline" style={{ color: primaryColor }}>Privacy Policy</a> : <span>Privacy Policy</span>}
                  {' '}and{' '}
                  {termsUrl ? <a href={termsUrl} target="_blank" rel="noreferrer" className="underline" style={{ color: primaryColor }}>Terms of Service</a> : <span>Terms of Service</span>}.
                </span>
              </label>
            </div>
          )}
          <Button
            className="text-white"
            style={{ backgroundColor: primaryColor }}
            onClick={() => onAdvance && onAdvance()}
            disabled={consentActive && !consentGiven}
            data-testid={`welcome-start-${question.id}`}
          >
            {buttonLabel}
          </Button>
          {ws.website_qr_url && (
            <a href={ws.website_qr_url} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 mt-6" data-testid={`welcome-website-qr-${question.id}`}>
              <QRCodeSVG value={ws.website_qr_url} size={96} level="M" />
              <span className="text-[11px] text-[#64748B]">{ws.website_qr_label || 'Visit our website'}</span>
            </a>
          )}
        </div>
      );
    }

    case 'short_text':
      return (
        <div>
          {renderLabel()}
          <Input value={answer || ''} onChange={(e) => onChange(e.target.value)} placeholder={renderPlaceholder} data-testid={`answer-${question.id}`} />
        </div>
      );

    case 'email': {
      const emailVal = String(answer || '').trim();
      const emailInvalid = emailVal.length > 0 && !EMAIL_RE.test(emailVal);
      return (
        <div>
          {renderLabel()}
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={254}
            value={answer || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={renderPlaceholder || 'name@example.com'}
            data-testid={`answer-${question.id}`}
          />
          {emailInvalid && (
            <p className="text-xs text-red-500 mt-1" data-testid={`email-error-${question.id}`}>
              Please use the email format name@example.com.
            </p>
          )}
        </div>
      );
    }

    case 'long_text':
      return (
        <div>
          {renderLabel()}
          <Textarea rows={4} value={answer || ''} onChange={(e) => onChange(e.target.value)} placeholder={renderPlaceholder} data-testid={`answer-${question.id}`} />
        </div>
      );

    case 'number_input': {
      const ns = question.number_settings || {};
      return (
        <div>
          {renderLabel()}
          <Input type="number" min={ns.min_value ?? undefined} max={ns.max_value ?? undefined} step={ns.step ?? 1}
            value={answer ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={renderPlaceholder} data-testid={`answer-${question.id}`} />
        </div>
      );
    }

    case 'dropdown': {
      const opts = question.options || [];
      // Fix 1: ensure long option labels (e.g. branch names up to ~60 chars)
      // render in full — disable any inherited text-overflow / single-line
      // truncation, and allow the native select panel to grow as wide as
      // its widest option.
      return (
        <div>
          {renderLabel()}
          <select value={answer ?? ''} onChange={(e) => onChange(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm bg-white"
            style={{ minWidth: '100%', whiteSpace: 'normal', textOverflow: 'clip', wordBreak: 'break-word' }}
            data-testid={`answer-${question.id}`}>
            <option value="">Choose…</option>
            {opts.map((o, i) => {
              const label = typeof o === 'string' ? o : (o.label ?? '');
              return (
                <option
                  key={i}
                  value={label}
                  title={label}
                  style={{ whiteSpace: 'normal' }}
                >
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    case 'single_choice': {
      const opts = question.options || [];
      const activeFollowup = followupFor(question, answer);
      return (
        <div>
          {renderLabel()}
          <div className="space-y-2">
            {opts.map((o, i) => {
              const label = typeof o === 'string' ? o : (o.label ?? '');
              const score = typeof o === 'object' ? o.score_value : null;
              const selected = answer?.label === label;
              return (
                <React.Fragment key={i}>
                  <button
                    onClick={() => onChange({ label, score_value: score })}
                    className={`w-full flex items-center gap-3 border rounded-lg px-3 py-2.5 text-sm text-left transition ${selected ? 'border-2' : 'border-[#E2E8F0]'}`}
                    style={selected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}0A` } : {}}
                    data-testid={`option-${question.id}-${i}`}
                  >
                    <span className={`w-4 h-4 shrink-0 rounded-full border-2 ${selected ? '' : 'border-[#CBD5E1]'}`} style={selected ? { borderColor: primaryColor, backgroundColor: primaryColor } : {}} />
                    {label}
                  </button>
                  {selected && activeFollowup && sameOption(activeFollowup.option, label) && (
                    <ChoiceFollowup
                      cfg={activeFollowup}
                      qid={question.id}
                      value={followupValues[activeFollowup.option]}
                      onChange={(v) => onFollowupChange(activeFollowup.option, v)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );
    }

    case 'multiple_choice': {
      const opts = question.options || [];
      const selected = Array.isArray(answer) ? answer : [];
      const toggle = (obj) => {
        const exists = selected.findIndex(s => s.label === obj.label);
        const next = exists >= 0 ? selected.filter((_, i) => i !== exists) : [...selected, obj];
        onChange(next);
      };
      return (
        <div>
          {renderLabel()}
          <div className="space-y-2">
            {opts.map((o, i) => {
              const label = typeof o === 'string' ? o : (o.label ?? '');
              const score = typeof o === 'object' ? o.score_value : null;
              const isOn = selected.some(s => s.label === label);
              return (
                <button
                  key={i}
                  onClick={() => toggle({ label, score_value: score })}
                  className={`w-full flex items-center gap-3 border rounded-lg px-3 py-2.5 text-sm text-left transition ${isOn ? 'border-2' : 'border-[#E2E8F0]'}`}
                  style={isOn ? { borderColor: primaryColor, backgroundColor: `${primaryColor}0A` } : {}}
                  data-testid={`option-${question.id}-${i}`}
                >
                  <span className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${isOn ? '' : 'border-[#CBD5E1]'}`} style={isOn ? { borderColor: primaryColor, backgroundColor: primaryColor } : {}}>
                    {isOn && <Check className="w-3 h-3 text-white" />}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'button_choice':
    case 'yes_no': {
      // Defensive default: a yes_no question must always offer Yes/No buttons.
      // Some legacy / drag-from-picker questions persisted with `options: []`
      // before the picker was forwarding the type defaults — fall back so the
      // public form is never unanswerable.
      let opts = question.options || [];
      if (question.type === 'yes_no' && opts.length < 2) {
        opts = [
          { label: 'Yes', score_value: 1 },
          { label: 'No', score_value: 0 },
        ];
      }
      // Desktop ≥768px: lay all options out in a single row using grid-flow-col
      // + auto-cols-fr so each option fits a fair share of the wider card,
      // instead of wrapping to two rows.
      const mobileGrid = opts.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';
      return (
        <div>
          {renderLabel()}
          <div className={`grid gap-2 ${mobileGrid} md:grid-cols-none md:grid-flow-col md:auto-cols-fr`}>
            {opts.map((o, i) => {
              const label = typeof o === 'string' ? o : (o.label ?? '');
              const score = typeof o === 'object' ? o.score_value : null;
              const selected = answer?.label === label;
              return (
                <button
                  key={i}
                  onClick={() => onChange({ label, score_value: score })}
                  className="px-4 py-4 rounded-xl border-2 text-sm font-medium transition"
                  style={selected
                    ? { borderColor: primaryColor, backgroundColor: primaryColor, color: '#fff' }
                    : { borderColor: '#E2E8F0', color: '#334155', backgroundColor: '#fff' }}
                  data-testid={`option-${question.id}-${i}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'star_rating': {
      const max = question.star_settings?.max_stars ?? 5;
      if (isStd2026) {
        return (
          <div>
            {renderLabel()}
            <StarRating2026 question={question} answer={answer} onChange={onChange} />
          </div>
        );
      }
      return (
        <div>
          {renderLabel()}
          <div className="flex gap-1 justify-center">
            {Array.from({ length: max }).map((_, i) => {
              const val = i + 1;
              const active = (answer ?? 0) >= val;
              return (
                <button key={i} onClick={() => onChange(val)} data-testid={`star-${question.id}-${val}`} aria-label={`${val} stars`}>
                  <Star className="w-10 h-10" fill={active ? '#F59E0B' : 'transparent'} stroke={active ? '#F59E0B' : '#CBD5E1'} />
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'rating_scale': {
      const rs = question.rating_scale || { min_value: 1, max_value: 5, min_label: '', max_label: '' };
      const values = [];
      for (let v = rs.min_value; v <= rs.max_value; v++) values.push(v);
      return (
        <div>
          {renderLabel()}
          <div className="flex gap-2 justify-center flex-wrap">
            {values.map((v) => {
              const selected = answer === v;
              return (
                <button key={v} onClick={() => onChange(v)}
                  className="w-11 h-11 rounded-lg border-2 text-sm font-semibold transition"
                  style={selected ? { borderColor: primaryColor, backgroundColor: primaryColor, color: '#fff' } : { borderColor: '#E2E8F0', color: '#334155', backgroundColor: '#fff' }}
                  data-testid={`rating-${question.id}-${v}`}>
                  {v}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between text-[11px] text-[#64748B] mt-2 px-1">
            <span>{rs.min_label}</span><span>{rs.max_label}</span>
          </div>
        </div>
      );
    }

    case 'emoji_rating': {
      // Phase 2 — deterministic 1..N answer. Neutral SVG faces so we
      // don't need to host new assets and the visual stays consistent
      // across surveys.
      const es = question.emoji_settings || { scale: 5 };
      const scale = [3, 5].includes(Number(es.scale)) ? Number(es.scale) : 5;
      const labels = Array.isArray(es.labels) ? es.labels : [];
      const values = Array.from({ length: scale }, (_, i) => i + 1);
      return (
        <div>
          {renderLabel()}
          <div
            className="grid mt-3 gap-2"
            style={{ gridTemplateColumns: `repeat(${scale}, minmax(0, 1fr))` }}
            data-testid={`emoji-rating-${question.id}`}
          >
            {values.map(v => (
              <EmojiRatingFace
                key={v}
                score={v}
                total={scale}
                isSelected={answer === v}
                onClick={() => onChange(v)}
                label={labels[v - 1] || ''}
              />
            ))}
          </div>
        </div>
      );
    }

    case 'ranking': {
      // Phase 2 — respondent orders options top→bottom. Response is
      // stored as an ordered array of stable option IDs so subsequent
      // label edits by the survey author never invalidate historical
      // responses.
      const opts = (question.options || []).map((o) => (typeof o === 'string')
        ? { id: o, label: o }
        : { id: o.id || o.label, label: o.label ?? '' });
      const currentOrder = Array.isArray(answer) && answer.length === opts.length
        ? answer
        : opts.map(o => o.id);
      const orderedOpts = currentOrder.map(id => opts.find(o => o.id === id)).filter(Boolean);
      const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= orderedOpts.length) return;
        const next = orderedOpts.map(o => o.id);
        [next[idx], next[j]] = [next[j], next[idx]];
        onChange(next);
      };
      return (
        <div>
          {renderLabel()}
          <p className="text-[11px] text-[#64748B] mb-2">Drag to reorder, or use the arrows on the right. Top = highest priority.</p>
          <RankingList
            orderedOpts={orderedOpts}
            questionId={question.id}
            onReorder={(ids) => onChange(ids)}
            onMoveUp={(i) => move(i, -1)}
            onMoveDown={(i) => move(i, 1)}
          />
        </div>
      );
    }

    case 'image_choice': {
      // Phase 2 — image options are the answers. Single/multiple mode
      // decided by `image_choice_settings.mode`. Response mirrors
      // single/multiple_choice serialization ({id,label,score_value})
      // so downstream reporting stays uniform.
      const cfg = question.image_choice_settings || { mode: 'single' };
      const mode = cfg.mode === 'multiple' ? 'multiple' : 'single';
      const opts = (question.options || []).map((o) => (typeof o === 'string')
        ? { id: o, label: o, image_url: '' }
        : { id: o.id || o.label, label: o.label ?? '', image_url: o.image_url || '' });
      const selectedIds = mode === 'multiple'
        ? (Array.isArray(answer) ? answer.map(a => a && a.id).filter(Boolean) : [])
        : (answer && answer.id ? [answer.id] : []);
      const toggle = (o) => {
        const payload = { id: o.id, label: o.label, score_value: null };
        if (mode === 'single') {
          onChange(selectedIds[0] === o.id ? null : payload);
        } else {
          const cur = Array.isArray(answer) ? answer : [];
          const exists = cur.findIndex(x => x && x.id === o.id);
          const next = exists >= 0 ? cur.filter((_, i) => i !== exists) : [...cur, payload];
          onChange(next);
        }
      };
      return (
        <div>
          {renderLabel()}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 auto-rows-fr items-stretch" data-testid={`imgc-${question.id}`}>
            {opts.map((o, i) => {
              const isOn = selectedIds.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o)}
                  className="flex flex-col items-stretch h-full rounded-lg border-2 overflow-hidden transition text-left"
                  style={isOn ? { borderColor: primaryColor, boxShadow: `0 6px 20px -8px ${primaryColor}55` } : { borderColor: '#E2E8F0' }}
                  data-testid={`imgc-option-${question.id}-${i}`}
                  aria-pressed={isOn}
                >
                  {/* Fixed 4:3 image area — identical display size for every
                      option. Uses a padding-ratio box with an absolutely
                      positioned image so the height is enforced regardless of
                      the flex-column context or the image's intrinsic size
                      (a plain aspect-ratio div can be stretched taller by a
                      portrait image via flex min-height:auto). The <img> fills
                      the box 100%/100% with object-cover + centre position, so
                      portrait images (e.g. Marbella) are cropped to the same
                      4:3 area as landscape ones. Source files are untouched. */}
                  <div className="relative w-full bg-[#F8FAFC] overflow-hidden shrink-0" style={{ paddingBottom: '75%' }}>
                    {o.image_url
                      ? <img src={toAbsoluteUrl(o.image_url)} alt={o.label || ''} className="absolute inset-0 w-full h-full object-cover object-center" />
                      : <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#94A3B8]">No image</div>}
                  </div>
                  <div className="flex-1 px-2 py-1.5 flex items-start justify-between gap-1.5 text-xs">
                    <span className="text-[#0F172A] font-medium break-words">{o.label}</span>
                    {isOn && <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: primaryColor }} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'nps': {
      const values = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      const { style, appearance } = resolveNpsPresentation(question);
      const hasAnswer = typeof answer === 'number';
      const isNumbers = style === 'numbers';
      if (isStd2026) {
        return (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-center mb-1" style={{ color: primaryColor }}>Net Promoter&reg; Score</p>
            {renderLabel()}
            <NpsFaces2026 question={question} answer={answer} onChange={onChange} primaryColor={primaryColor} />
          </div>
        );
      }
      return (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-center mb-1" style={{ color: primaryColor }}>Net Promoter&reg; Score</p>
          {renderLabel()}
          {hasAnswer && (
            <div className="flex justify-center mt-3" data-testid={`nps-selected-chip-${question.id}`}>
              <span
                className="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3 py-1.5"
                style={{ background: '#F1F5F9', color: '#334155' }}
              >
                You selected <span className="text-sm font-bold">{answer}</span>
              </span>
            </div>
          )}
          <div
            className={`mt-4 pt-3 ${isNumbers ? 'grid grid-cols-11 gap-[3px] sm:gap-1.5' : 'flex justify-between items-end gap-[1px] sm:gap-[2px]'}`}
            style={{ overflow: 'visible' }}
            data-testid={`nps-scale-${question.id}`}
            data-nps-style={style}
            data-nps-appearance={appearance}
          >
            {values.map(v => (
              <NpsScaleOption
                key={v}
                score={v}
                style={style}
                appearance={appearance}
                isSelected={answer === v}
                onSelect={onChange}
                primaryColor={primaryColor}
              />
            ))}
          </div>
          <div className="flex justify-between px-1 mt-3">
            <span className="text-xs sm:text-sm font-semibold text-[#334155]" data-testid="nps-label-0">Not at all likely</span>
            <span className="text-xs sm:text-sm font-semibold text-[#334155]" data-testid="nps-label-10">Extremely likely</span>
          </div>
        </div>
      );
    }

    case 'date':
      return (
        <div>
          {renderLabel()}
          <Input type="date" value={answer || ''} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${question.id}`} />
        </div>
      );

    case 'time':
      return (
        <div>
          {renderLabel()}
          <Input type="time" value={answer || ''} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${question.id}`} />
        </div>
      );

    case 'file_upload': {
      return (
        <div>
          {renderLabel()}
          <FileUploadQuestion
            question={question}
            answer={answer}
            onChange={onChange}
            branchId={branchId}
            onUploadingChange={onUploadingChange}
            isPreview={isPreview}
          />
        </div>
      );
    }

    case 'voice': {
      return (
        <div>
          {renderLabel()}
          <VoiceOrTypeAnswer
            key={`voice-${step}-${question.id}`}
            question={question}
            answer={answer}
            primaryColor={primaryColor}
            onChange={onChange}
            onVoiceBusyChange={onVoiceBusyChange}
            sessionKey={sessionKey}
            lockRecord={promptAudioPlaying}
          />
        </div>
      );
    }

    case 'media_question': {
      // Standalone Media Question — respondent plays the audio/video prompt
      // (native controls, NO autoplay) and answers in the configured format.
      const ms = question.media_settings || { kind: 'audio', media_url: '', answer_format: 'long_text' };
      const mediaU = ms.media_url ? toAbsoluteUrl(ms.media_url) : '';
      return (
        <div>
          {renderLabel()}
          {mediaU && (
            <div className="mb-4" data-testid={`media-prompt-${question.id}`}>
              {ms.kind === 'video'
                ? <video src={mediaU} controls playsInline preload="metadata" className="w-full rounded-lg border border-[#E2E8F0]" data-testid={`media-prompt-video-${question.id}`} />
                : <audio src={mediaU} controls preload="metadata" className="w-full" data-testid={`media-prompt-audio-${question.id}`} {...audioPlayHandlers} />}
            </div>
          )}
          {ms.answer_format === 'voice' ? (
            <VoiceOrTypeAnswer
              key={`media-voice-${step}-${question.id}`}
              question={question}
              primaryColor={primaryColor}
              onChange={onChange}
              onVoiceBusyChange={onVoiceBusyChange}
              sessionKey={sessionKey}
              lockRecord={promptAudioPlaying}
            />
          ) : (
            <Textarea
              rows={4}
              value={typeof answer === 'string' ? answer : ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder_text || 'Type your answer here…'}
              data-testid={`answer-${question.id}`}
              style={{ borderColor: '#E2E8F0' }}
            />
          )}
        </div>
      );
    }

    case 'confirmation': {
      const cs = question.confirmation_settings || { button_label: 'I Agree' };
      const confirmed = !!answer;
      return (
        <div>
          {renderLabel()}
          <Button onClick={() => onChange(!confirmed)}
            className="w-full"
            style={{ backgroundColor: confirmed ? '#22C55E' : primaryColor, color: '#fff' }}
            data-testid={`answer-${question.id}`}>
            {confirmed ? <><Check className="w-4 h-4 mr-1" /> Confirmed</> : cs.button_label}
          </Button>
        </div>
      );
    }

    case 'contact_info': {
      const cf = question.contact_fields || { mobile: { enabled: true, required: false }, email: { enabled: false, required: false }, first_name: { enabled: false, required: false }, last_name: { enabled: false, required: false } };
      const val = answer || {};
      const setField = (k, v) => onChange({ ...val, [k]: v });

      // standard_2026 opt-in pattern (driven by `contact_optin` on the question,
      // so legacy_v1 surveys are untouched): the contact fields are shown up
      // front and optional; a No/Yes preference question sits BELOW them.
      // Selecting "Yes" makes a contact method required; "No" / no selection
      // keeps everything optional. Consent is NOT collected here for
      // standard_2026 — it lives on the Welcome screen.
      const optIn = isStd2026 && !!question.contact_optin;
      const contactPref = val.contact_pref; // undefined | 'no' | 'yes'
      const fieldsRequired = optIn ? (contactPref === 'yes') : null;
      const reqMark = (metaRequired) => ((optIn ? fieldsRequired : metaRequired) ? ' *' : '');

      // Mobile is stored as { country_code, number } so the backend can route
      // SMS / WhatsApp follow-ups correctly. Older saved answers might still
      // be plain strings — coerce on first read.
      const mobileVal = (val.mobile && typeof val.mobile === 'object')
        ? val.mobile
        : { country_code: defaultDialCode || DEFAULT_DIAL_CODE, number: typeof val.mobile === 'string' ? val.mobile : '' };
      const setMobile = (patch) => setField('mobile', { ...mobileVal, ...patch });
      const mobileRequired = optIn ? fieldsRequired : cf.mobile?.required;
      const numberDigitsOnly = mobileVal.number || '';
      const mobileInvalid = mobileRequired
        ? (!mobileVal.country_code || !MOBILE_NUMBER_RE.test(numberDigitsOnly))
        : (numberDigitsOnly.length > 0 && !MOBILE_NUMBER_RE.test(numberDigitsOnly));

      const textFieldDef = [
        { key: 'first_name', label: 'First name', type: 'text' },
        { key: 'last_name', label: 'Last name', type: 'text' },
        { key: 'email', label: 'Email address', type: 'email' },
      ];
      return (
        <div>
          {!optIn && renderLabel()}
          <div className="space-y-2">
            {textFieldDef.map(f => cf[f.key]?.enabled && (
              <div key={f.key}>
                <label className="text-[11px] text-[#64748B]">{f.label}{reqMark(cf[f.key].required)}</label>
                <Input type={f.type} value={val[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)} data-testid={`contact-${f.key}-${question.id}`} />
              </div>
            ))}
            {cf.mobile?.enabled && (
              <div data-testid={`contact-mobile-wrap-${question.id}`}>
                <label className="text-[11px] text-[#64748B]">Mobile number{reqMark(cf.mobile.required)}</label>
                <div className="flex gap-2">
                  <select
                    value={mobileVal.country_code || DEFAULT_DIAL_CODE}
                    onChange={(e) => setMobile({ country_code: e.target.value })}
                    className="border border-[#E2E8F0] rounded-md text-sm px-2 py-2 bg-white max-w-[120px]"
                    data-testid={`contact-mobile-cc-${question.id}`}
                  >
                    {COUNTRY_DIAL_CODES.map(c => (
                      <option key={c.iso} value={c.phone}>{c.phone} ({c.iso})</option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={mobileVal.number || ''}
                    onChange={(e) => setMobile({ number: e.target.value.replace(/\D/g, '') })}
                    placeholder="9171234567"
                    data-testid={`contact-mobile-${question.id}`}
                  />
                </div>
                {mobileInvalid && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid={`contact-mobile-error-${question.id}`}>
                    {mobileRequired && !numberDigitsOnly
                      ? 'Mobile number is required.'
                      : 'Enter a valid mobile number (7–15 digits, numbers only).'}
                  </p>
                )}
              </div>
            )}
            {optIn && (
              <div className="pt-3" data-testid={`contact-optin-${question.id}`}>
                <p className="text-sm font-medium text-[#0F172A] mb-2">{cleanQuestionLabel(question.text)}</p>
                <div className="space-y-2">
                  {[{ v: 'no', label: 'No' }, { v: 'yes', label: 'Yes' }].map(opt => {
                    const selected = contactPref === opt.v;
                    return (
                      <button
                        type="button"
                        key={opt.v}
                        onClick={() => setField('contact_pref', opt.v)}
                        className="w-full flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors"
                        style={selected ? { borderColor: primaryColor, backgroundColor: '#F8FAFC' } : { borderColor: '#E2E8F0' }}
                        data-testid={`contact-optin-${opt.v}-${question.id}`}
                      >
                        <span className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0" style={{ borderColor: selected ? primaryColor : '#94A3B8' }}>
                          {selected && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />}
                        </span>
                        <span className="text-sm font-semibold text-[#0F172A]">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!optIn && showConsent !== false && (
              <label className="flex items-start gap-2 text-xs text-[#64748B] pt-2">
                <input type="checkbox" className="mt-0.5" checked={!!val.consent_given} onChange={(e) => setField('consent_given', e.target.checked)} data-testid={`contact-consent-${question.id}`} />
                <span>
                  I agree to provide feedback about {brandName || 'this business'}. My personal information will be handled in accordance with their{' '}
                  {privacyUrl ? <a href={privacyUrl} target="_blank" rel="noreferrer" className="underline" style={{ color: primaryColor }}>Privacy Policy</a> : <span>Privacy Policy</span>}
                  {' '}and{' '}
                  {termsUrl ? <a href={termsUrl} target="_blank" rel="noreferrer" className="underline" style={{ color: primaryColor }}>Terms of Service</a> : <span>Terms of Service</span>}.
                </span>
              </label>
            )}
          </div>
        </div>
      );
    }

    case 'section_header':
      return (
        <div className="py-2" data-testid={`section-${question.id}`}>
          <h3 className="text-lg font-bold text-[#0F172A]">
            {questionNumber && (
              <span className="text-[#64748B] mr-2" data-testid={`q-number-${question.id}`}>{questionNumber}.</span>
            )}
            {cleanQuestionLabel(question.text)}
          </h3>
          {question.sub_text && <p className="text-sm text-[#64748B] mt-1">{cleanQuestionLabel(question.sub_text)}</p>}
        </div>
      );

    case 'rich_text_block':
      return <div className="prose prose-sm max-w-none text-[#0F172A]" data-testid={`rich-${question.id}`} dangerouslySetInnerHTML={safeHtml(question.rich_content)} />;

    case 'image_display': {
      const is_ = question.image_settings || {};
      if (!is_.url) return null;
      return <img src={is_.url} alt={is_.alt || ''} className="w-full rounded-lg" data-testid={`image-${question.id}`} />;
    }

    case 'matrix':
      return (
        <MatrixQuestion
          question={question}
          answer={answer}
          onChange={onChange}
          primaryColor={primaryColor}
          questionNumber={questionNumber}
        />
      );

    case 'thank_you':
      // The thank_you block is structural — its settings drive the post-submit
      // screen. It should not appear in the question flow itself.
      return null;

    default:
      return <div className="text-xs text-red-500" data-testid={`unsupported-${question.type}`}>Unsupported question type: {question.type}</div>;
  }
}

// ─── Footer ───
function SurveyFooter({ footerSettings, primaryColor }) {
  const showPoweredBy = footerSettings?.show_powered_by !== false;
  const privacy = footerSettings?.privacy_policy_url;
  const customText = footerSettings?.custom_footer_text;
  return (
    <footer className="mt-6 pb-6 text-center text-[11px] text-[#94A3B8] space-y-1" data-testid="survey-footer">
      {customText && <p>{customText}</p>}
      {privacy && (
        <a href={privacy} target="_blank" rel="noreferrer" className="underline" style={{ color: primaryColor }} data-testid="footer-privacy-link">Privacy Policy</a>
      )}
      {showPoweredBy && (
        <p data-testid="footer-powered-by" className="flex items-center justify-center gap-1.5">
          <span>Powered by</span>
          <img
            src="https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png"
            alt="satisFIND"
            style={{ width: 80, height: 'auto', background: 'transparent', mixBlendMode: 'multiply' }}
            data-testid="footer-satisfind-logo"
          />
        </p>
      )}
    </footer>
  );
}

// ─── Main Survey Form ───
function PreviewBanner({ presentation, onRestart }) {
  return (
    <div className="w-full bg-[#0F172A] text-white text-[11px] px-4 py-2 flex items-center justify-between gap-2" data-testid="survey-preview-banner">
      <span>
        Preview · <b data-testid="survey-preview-experience">{presentation === 'standard_2026' ? 'Standard 2026' : 'Legacy'}</b> experience · responses are not saved
      </span>
      <button type="button" onClick={onRestart} className="underline shrink-0" data-testid="survey-preview-restart-btn">Restart</button>
    </div>
  );
}

// `previewSurveyId` ⇒ Survey Builder preview: loads any-status survey via the
// authenticated preview bundle and never submits or uploads.
export default function SurveyForm({ previewSurveyId = null, entry = null }) {
  const params = useParams();
  // Generic public entry (/go/:slug or a custom host): the branch is resolved from the access credential.
  const entryKey = useMemo(
    () => entry || (params.slug ? { slug: params.slug } : null),
    [entry, params.slug],
  );
  const entryQuery = entryKey ? new URLSearchParams(entryKey).toString() : '';
  const [resolved, setResolved] = useState(null);
  const branchId = resolved?.branch_id || params.branchId;
  const code = resolved?.code || params.code;
  const isPreview = !!previewSurveyId;
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  // Conditional follow-ups: { [questionId]: { [optionLabel]: value } }. Only
  // the selected option's value is validated/submitted; others are pruned
  // when the respondent leaves the screen.
  const [followups, setFollowups] = useState({});
  const setFollowup = (qid, option, value) => setFollowups(prev => ({ ...prev, [qid]: { ...(prev[qid] || {}), [option]: value } }));
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Phase 3 access credential: undefined = gate pending, null = skipped (optional), object = validated.
  const [accessCredential, setAccessCredential] = useState(undefined);
  // True once the access screen was actually shown (not when QR Direct Access skipped it).
  const [accessScreenSeen, setAccessScreenSeen] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  // Fix 4: track whether any file_upload question is currently uploading.
  // Map of question_id -> bool. Submit/Next are disabled while any upload is
  // in-flight to prevent the satisFINDER from submitting before files land.
  const [uploadingMap, setUploadingMap] = useState({});
  const anyUploading = Object.values(uploadingMap).some(Boolean);
  const setUploadingFor = useCallback((qid, busy) => {
    setUploadingMap(prev => {
      const next = { ...prev };
      if (busy) next[qid] = true; else delete next[qid];
      return next;
    });
  }, []);
  // IP-derived dial code default for the contact_info mobile field. Falls
  // back to DEFAULT_DIAL_CODE if ipapi is blocked or returns nothing.
  const [defaultDialCode, setDefaultDialCode] = useState(DEFAULT_DIAL_CODE);
  // Phase 1: localStorage-backed "Save as draft" for long surveys (mystery
  // shopping etc). Key shape: vox_draft__<survey_id>__<branch_id>. Stores
  // { answers, comments, step, savedAt }. Restored once on mount via banner.
  const draftKey = useMemo(() => {
    const sid = bundle?.survey?.survey_id || '';
    return sid && branchId ? `vox_draft__${sid}__${branchId}` : '';
  }, [bundle?.survey?.survey_id, branchId]);
  const [draftBanner, setDraftBanner] = useState(null); // { savedAt, answers, comments, step }
  const [draftSavedToast, setDraftSavedToast] = useState(null);
  const draftRestoredRef = useRef(false);

  // Detect a previously saved draft once the bundle has loaded.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.answers) {
        setDraftBanner(parsed);
      }
    } catch (_e) { /* ignore */ }
  }, [draftKey]);

  const saveDraft = () => {
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        answers, comments, followups, step,
        savedAt: new Date().toISOString(),
      }));
      setDraftSavedToast(new Date().toISOString());
      setTimeout(() => setDraftSavedToast(null), 3000);
    } catch (_e) { /* localStorage full or disabled */ }
  };

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch (_e) { /* ignore */ }
  };

  const resumeDraft = () => {
    if (!draftBanner) return;
    setAnswers(draftBanner.answers || {});
    setComments(draftBanner.comments || {});
    setFollowups(draftBanner.followups || {});
    setStep(Number.isInteger(draftBanner.step) ? draftBanner.step : 0);
    draftRestoredRef.current = true;
    setDraftBanner(null);
  };

  const discardDraft = () => {
    clearDraft();
    draftRestoredRef.current = true;
    setDraftBanner(null);
  };

  useEffect(() => {
    let cancelled = false;
    fetchIpCountryDial().then(dial => {
      if (!cancelled && dial) setDefaultDialCode(dial);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = isPreview
          ? await fetch(`${API}/surveys/builder/${previewSurveyId}/preview-bundle`, { credentials: 'include' })
          : await fetch(entryQuery ? `${API}/public/entry?${entryQuery}` : `${API}/public/survey/${params.branchId}/${params.code}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setErrorMsg(err.detail || 'This survey is not currently available.');
          return;
        }
        const data = await res.json();
        setBundle(data);
      } catch {
        setErrorMsg('This survey is not currently available.');
      } finally {
        setLoading(false);
      }
    })();
  }, [params.branchId, params.code, entryQuery, isPreview, previewSurveyId]);

  const primary = bundle?.tenant?.branding?.primary_color || DEFAULT_PRIMARY;
  const secondary = bundle?.tenant?.branding?.secondary_color || DEFAULT_SECONDARY;
  const logo = bundle?.tenant?.branding?.logo || SATISFIND_LOGO;
  const brandName = bundle?.tenant?.name || 'satisFIND';
  const presentation = (bundle?.survey?.presentation_version === 'standard_2026') ? 'standard_2026' : 'legacy_v1';
  const isStd2026 = presentation === 'standard_2026';
  const accessOn = !isPreview && !!bundle?.access?.enabled;
  // With access validation on, the gate replaces any in-flow access-code screen.
  const questions = useMemo(
    () => (bundle?.questions || []).filter(q => !(accessOn && q.type === 'survey_access_code')),
    [bundle, accessOn],
  );
  const numberMap = useMemo(() => computeQuestionNumbers(questions), [questions]);
  const footerSettings = bundle?.survey?.footer_settings || { show_powered_by: true };
  const privacyUrl = footerSettings.privacy_policy_url || '';
  // Survey-level "Show consent section" toggle (default ON for legacy surveys).
  const showConsent = bundle?.survey?.show_consent !== false;
  // Phase 1: when ON, render every question under a section header on the
  // same scrollable page (one Next click advances past the entire section).
  // Default OFF — legacy surveys keep one-question-per-page behaviour.
  const sectionGrouped = !!bundle?.survey?.section_grouped_display;
  // NPS® trademark footer: explicit override on the survey, else default ON
  // when an NPS question exists, OFF otherwise.
  const hasNpsQuestion = useMemo(() => (bundle?.questions || []).some(q => q?.type === 'nps'), [bundle]);
  const showNpsTrademark = (() => {
    const v = bundle?.survey?.show_nps_trademark_footer;
    if (v === undefined || v === null) return hasNpsQuestion;
    return !!v;
  })();

  const currentQuestion = questions[step];

  // ─── Conditional visibility ────────────────────────────────────────────
  // Evaluate each question's `condition` against the current answers and
  // return the set of question IDs that should be hidden. Hidden questions
  // are skipped during navigation and excluded from the submitted payload.
  const isConditionMet = (cond, answersState) => {
    if (!cond || !cond.question_id) return true;
    const parent = answersState[cond.question_id];
    // Choice-style answers may be stored as a primitive label/string OR as
    // a `{ value, comment }` wrapper OR as `{ label, value }`. Normalise.
    let raw = parent;
    if (raw && typeof raw === 'object') {
      if ('label' in raw) raw = raw.label;
      else if ('value' in raw) raw = raw.value;
    }
    const parentVal = raw == null ? '' : String(raw);
    const target = cond.value == null ? '' : String(cond.value);
    const op = cond.operator || 'eq';
    // The legacy question-level `condition` uses short opcodes (eq/neq).
    // The newer logic-rules editor uses long form (equals / not_equals /
    // greater_than / less_than / contains). Support both dialects.
    switch (op) {
      case 'eq':
      case 'equals':
        return parentVal === target;
      case 'neq':
      case 'not_equals':
        return parentVal !== target;
      case 'contains':
        return parentVal.toLowerCase().includes(target.toLowerCase());
      case 'greater_than': {
        const a = Number(parentVal), b = Number(target);
        return Number.isFinite(a) && Number.isFinite(b) && a > b;
      }
      case 'less_than': {
        const a = Number(parentVal), b = Number(target);
        return Number.isFinite(a) && Number.isFinite(b) && a < b;
      }
      default:
        return parentVal === target;
    }
  };

  // Navigation hide-set: a conditional question is hidden when its parent's
  // answer fails the rule. Thank-you blocks are ALSO treated as out-of-flow:
  // their settings drive the post-submit "thank you" screen rather than being
  // rendered inline, so Submit naturally lands on the last real question.
  const hiddenIds = useMemo(() => {
    const out = new Set();
    for (const q of questions) {
      if (q.type === 'thank_you') { out.add(q.id); continue; }
      if (q.condition && q.condition.question_id) {
        if (!isConditionMet(q.condition, answers)) out.add(q.id);
      }
    }
    // ── Skip-to logic: when a `skip_to` rule attached to any question is
    // active (its condition is satisfied against the current answers) AND
    // its source has been reached in the survey, treat every question
    // strictly between the source and the target as skipped. Same effect
    // as hidden — the runtime already scrubs their answers and steps past
    // them, and the Next button walks straight to the target.
    const idToIndex = new Map();
    questions.forEach((q, i) => idToIndex.set(q.id, i));
    for (const q of questions) {
      const rules = q.logic_rules || [];
      for (const r of rules) {
        if (r.action !== 'skip_to' || !r.target_question_id) continue;
        const srcIdx = idToIndex.get(q.id);
        const tgtIdx = idToIndex.get(r.target_question_id);
        if (srcIdx === undefined || tgtIdx === undefined) continue;
        if (tgtIdx <= srcIdx) continue;   // ignore rules that jump backwards or to self
        // A skip rule fires only once the source question has been answered
        // (so the respondent is committing to this branch). The condition
        // being checked is on `condition_question_id` (usually equals `q.id`).
        const condQid = r.condition_question_id || q.id;
        if (answers[condQid] === undefined || answers[condQid] === null) continue;
        const cond = {
          question_id: condQid,
          operator: r.operator || 'equals',
          value: r.condition_value,
        };
        if (!isConditionMet(cond, answers)) continue;
        // Mark every question strictly between source and target as skipped.
        for (let i = srcIdx + 1; i < tgtIdx; i++) {
          out.add(questions[i].id);
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, answers]);

  // Count hide-set: only hide a conditional question for the answerable
  // tally when its parent has been answered AND the answer doesn't match.
  // This keeps the "X answerable" count starting at the maximum and only
  // shrinking — never growing — as the respondent progresses, which is the
  // expected progress-bar UX.
  const countHiddenIds = useMemo(() => {
    const out = new Set();
    for (const q of questions) {
      const cond = q.condition;
      if (!cond || !cond.question_id) continue;
      const parentAns = answers[cond.question_id];
      if (parentAns === undefined || parentAns === null) continue;
      // Parent has been answered — evaluate the rule for real.
      if (!isConditionMet(cond, answers)) out.add(q.id);
    }
    // Include questions bypassed by an active skip_to rule so the progress
    // bar's "answerable" count only shrinks, never grows, as the respondent
    // commits to answers.
    const idToIndex = new Map();
    questions.forEach((q, i) => idToIndex.set(q.id, i));
    for (const q of questions) {
      for (const r of (q.logic_rules || [])) {
        if (r.action !== 'skip_to' || !r.target_question_id) continue;
        const srcIdx = idToIndex.get(q.id);
        const tgtIdx = idToIndex.get(r.target_question_id);
        if (srcIdx === undefined || tgtIdx === undefined || tgtIdx <= srcIdx) continue;
        const condQid = r.condition_question_id || q.id;
        if (answers[condQid] === undefined || answers[condQid] === null) continue;
        const met = isConditionMet(
          { question_id: condQid, operator: r.operator || 'equals', value: r.condition_value },
          answers,
        );
        if (!met) continue;
        for (let i = srcIdx + 1; i < tgtIdx; i++) out.add(questions[i].id);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, answers]);

  // When a previous answer changes such that a dependent question becomes
  // hidden, drop its stored answer + comment so it cannot leak into the
  // submitted payload.
  useEffect(() => {
    if (hiddenIds.size === 0) return;
    setAnswers(prev => {
      let changed = false;
      const next = { ...prev };
      for (const qid of hiddenIds) {
        if (qid in next) { delete next[qid]; changed = true; }
      }
      return changed ? next : prev;
    });
    setComments(prev => {
      let changed = false;
      const next = { ...prev };
      for (const qid of hiddenIds) {
        if (qid in next) { delete next[qid]; changed = true; }
      }
      return changed ? next : prev;
    });
    setFollowups(prev => {
      let changed = false;
      const next = { ...prev };
      for (const qid of hiddenIds) {
        if (qid in next) { delete next[qid]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [hiddenIds]);

  // Leaving a screen: keep only follow-up values for the option still
  // selected (an earlier option's typed value is restorable only while the
  // respondent stays on that screen).
  useEffect(() => {
    setFollowups(prev => {
      let changed = false;
      const next = {};
      for (const [qid, byOpt] of Object.entries(prev)) {
        const sel = selectedLabel(answers[qid]);
        const keep = Object.fromEntries(Object.entries(byOpt || {}).filter(([opt]) => sel && sameOption(opt, sel)));
        if (Object.keys(keep).length !== Object.keys(byOpt || {}).length) changed = true;
        if (Object.keys(keep).length) next[qid] = keep;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-skip the current step if the user lands on (or back into) a
  // question whose condition is no longer met.
  useEffect(() => {
    if (!currentQuestion) return;
    if (!hiddenIds.has(currentQuestion.id)) return;
    setStep(s => {
      // Try moving forward first; if all subsequent questions are hidden,
      // fall back to the nearest previous visible question.
      for (let i = s + 1; i < questions.length; i++) {
        if (!hiddenIds.has(questions[i].id)) return i;
      }
      for (let i = s - 1; i >= 0; i--) {
        if (!hiddenIds.has(questions[i].id)) return i;
      }
      return s;
    });
  }, [hiddenIds, currentQuestion, questions]);

  const nextVisibleStep = (from) => {
    // In section-grouped mode, the "current step" may span multiple
    // underlying question indices (a section header + all its children).
    // Skip forward past the entire span so Next lands on the next visible
    // standalone question or section header.
    let cursor = from;
    if (sectionGrouped && questions[cursor]?.type === 'section_header') {
      for (let i = cursor + 1; i < questions.length; i++) {
        if (questions[i].type === 'section_header') { cursor = i - 1; break; }
        if (i === questions.length - 1) { cursor = i; break; }
      }
    }
    // Skip past any inline-grouped followers of the current screen.
    while (cursor + 1 < questions.length && questions[cursor + 1]?.inline_with_prev) cursor++;
    for (let i = cursor + 1; i < questions.length; i++) {
      if (!hiddenIds.has(questions[i].id)) return i;
    }
    return questions.length - 1;
  };
  const prevVisibleStep = (from) => {
    for (let i = from - 1; i >= 0; i--) {
      if (hiddenIds.has(questions[i].id)) continue;
      // In section-grouped mode, when stepping back into a section's child,
      // rewind to the section header so the whole section re-renders.
      if (sectionGrouped) {
        for (let j = i; j >= 0; j--) {
          if (questions[j].type === 'section_header') return j;
          if (j === 0) return i;
        }
      }
      // Inline grouping: rewind to the start of the group so the whole
      // screen re-renders rather than landing on a follower.
      let g = i;
      while (g > 0 && questions[g]?.inline_with_prev) g--;
      return g;
    }
    return 0;
  };

  // ─── Section-grouped step members ──────────────────────────────────────
  // When the current step lands on a section_header in grouped mode, return
  // ALL question indices belonging to that section (header + every question
  // until the next section_header or end). In legacy mode this is always a
  // single-element array containing just `step`.
  const currentStepMembers = useMemo(() => {
    if (!questions.length) return [];
    if (!currentQuestion) return [step];
    // Section-grouped mode: a section header owns every following question
    // until the next header.
    if (sectionGrouped && currentQuestion.type === 'section_header') {
      const out = [step];
      for (let i = step + 1; i < questions.length; i++) {
        if (questions[i].type === 'section_header') break;
        out.push(i);
      }
      return out;
    }
    // Inline grouping: absorb immediately-following questions flagged
    // `inline_with_prev` so they render on the same screen/step.
    const out = [step];
    for (let i = step + 1; i < questions.length; i++) {
      if (questions[i]?.inline_with_prev) out.push(i);
      else break;
    }
    return out;
  }, [questions, step, sectionGrouped, currentQuestion]);

  const visibleStepMembers = useMemo(() =>
    currentStepMembers.filter(i => !hiddenIds.has(questions[i]?.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStepMembers, hiddenIds, questions]
  );

  // "Last" once there are no further visible questions after `step`. In
  // section-grouped mode we look past the entire current section span.
  const isLast = useMemo(() => {
    let from = step;
    if (sectionGrouped && questions[from]?.type === 'section_header') {
      for (let i = from + 1; i < questions.length; i++) {
        if (questions[i].type === 'section_header') { from = i - 1; break; }
        if (i === questions.length - 1) { from = i; break; }
      }
    }
    // Inline-grouped followers live on the same screen — step past them so a
    // screen whose only remaining siblings are inline is still the last step.
    while (from + 1 < questions.length && questions[from + 1]?.inline_with_prev) from++;
    for (let i = from + 1; i < questions.length; i++) {
      if (!hiddenIds.has(questions[i].id)) return false;
    }
    return true;
  }, [step, questions, hiddenIds, sectionGrouped]);

  // Non-question blocks (welcome / section_header / rich_text_block /
  // image_display / thank_you) are NOT counted in the answerable tally.
  // Build a per-step map so the progress label reads "Question X of Y"
  // in answerable terms only, and falls back to "Section X of Y" / similar
  // on structural blocks.
  const NON_ANSWERABLE_TYPES = ['section_header','rich_text_block','image_display','welcome','thank_you'];
  const answerableProgress = useMemo(() => {
    const positions = new Array(questions.length).fill(0);
    let counter = 0;
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const isAnswerable = !NON_ANSWERABLE_TYPES.includes(q.type) && !countHiddenIds.has(q.id);
      if (isAnswerable) {
        counter += 1;
        positions[i] = counter;
      } else {
        // structural / hidden block — show the previous answerable position
        positions[i] = counter;
      }
    }
    return { positions, total: counter };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, countHiddenIds]);

  const visibleCount = answerableProgress.total;
  const isAnswerableCurrent = currentQuestion && !NON_ANSWERABLE_TYPES.includes(currentQuestion.type) && !countHiddenIds.has(currentQuestion.id);
  const currentAnswerablePos = answerableProgress.positions[step] || 0;
  const progress = answerableProgress.total
    ? Math.round((Math.max(currentAnswerablePos, 1) / answerableProgress.total) * 100)
    : 0;

  // standard_2026 — screen-based "Step N of X" counter. EVERY rendered screen
  // counts (access code, welcome, grouped question screens, thank-you).
  // Inline-grouped questions (`inline_with_prev`) collapse into their parent
  // screen. The trailing thank-you/done screen is always the final step.
  const stdScreens = useMemo(() => {
    const groups = [];
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (q.type === 'thank_you') continue;       // trailing done screen
      if (hiddenIds.has(q.id)) continue;           // conditionally hidden
      if (q.inline_with_prev && groups.length) groups[groups.length - 1].push(i);
      else groups.push([i]);
    }
    return groups;
  }, [questions, hiddenIds]);
  // Phase 3: the access screen counts only when the respondent actually saw it.
  const stdAccessSteps = accessScreenSeen ? 1 : 0;
  const stdTotalSteps = stdScreens.length + 1 + stdAccessSteps;
  const stdCurrentStep = useMemo(() => {
    if (done) return stdTotalSteps;
    const gi = stdScreens.findIndex(g => g.includes(step));
    return (gi >= 0 ? gi + 1 : 1) + stdAccessSteps;
  }, [done, stdScreens, step, stdTotalSteps, stdAccessSteps]);
  const stdProgressPct = stdTotalSteps ? Math.round((stdCurrentStep / stdTotalSteps) * 100) : 0;

  const setAnswer = (qid, value) => setAnswers(prev => {
    if (value === undefined) {
      if (!(qid in prev)) return prev;
      const { [qid]: _, ...rest } = prev; // eslint-disable-line no-unused-vars
      return rest;
    }
    return { ...prev, [qid]: value };
  });

  // Reset stored voice answer whenever the respondent (re)enters a voice question.
  // Combined with the keyed VoiceOrTypeAnswer remount, this ensures: voice recorder
  // shown by default, any typed text cleared, any recorded audio cleared on revisit.
  // Text-first (Text + Optional Voice) keeps its answer and rebuilds from it instead.
  useEffect(() => {
    const isVoiceStep = (currentQuestion?.type === 'voice' && currentQuestion?.response_mode !== 'text_first')
      || (currentQuestion?.type === 'media_question' && (currentQuestion?.media_settings?.answer_format === 'voice'));
    if (!isVoiceStep) return;
    const qid = currentQuestion.id;
    setAnswers(prev => {
      if (!(qid in prev)) return prev;
      const { [qid]: _, ...rest } = prev; // eslint-disable-line no-unused-vars
      return rest;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, currentQuestion?.id, currentQuestion?.type]);

  // Phase 4A: Contact Preference "Yes" makes its linked contact field required.
  const contactYesLinked = useMemo(() => {
    const s = new Set();
    for (const q of questions) {
      const lid = q.type === 'contact_preference' && q.contact_preference_settings?.linked_question_id;
      if (lid && !hiddenIds.has(q.id) && prefChoice(answers[q.id]) === 'yes') s.add(lid);
    }
    return s;
  }, [questions, hiddenIds, answers]);
  const effectiveQ = (q) => (q && contactYesLinked.has(q.id) && !q.is_required ? { ...q, is_required: true } : q);

  // Per-question validation helper — used by both legacy (single-question
  // step) and section-grouped (multi-question step) modes.
  const isQuestionAnswered = (rawQ) => {
    const q = effectiveQ(rawQ);
    if (!q) return true;
    if (hiddenIds.has(q.id)) return true;
    if (['section_header','rich_text_block','image_display'].includes(q.type)) return true;

    // Conditional follow-up on the selected option (required / valid value).
    const fuCfg = followupFor(q, answers[q.id]);
    if (fuCfg && !isFollowupValid(fuCfg, followups[q.id]?.[fuCfg.option])) return false;

    if (q.type === 'mobile_or_email') {
      const v = String(answers[q.id] ?? '').trim();
      if (!v) return !q.is_required;
      return isValidMobileOrEmail(v);
    }

    if (q.type === 'contact_preference') {
      const v = prefChoice(answers[q.id]);
      if (v !== 'yes' && v !== 'no') return !q.is_required; // neither pre-selected
      const cps = q.contact_preference_settings || {};
      if (v === 'yes' && cps.ask_name_on_yes && cps.require_name_on_yes !== false && !prefName(answers[q.id]).trim()) return false;
      const lq = v === 'yes' && questions.find(x => x.id === cps.linked_question_id);
      if (lq && !hiddenIds.has(lq.id) && !isQuestionAnswered({ ...lq, is_required: true })) return false;
      return true;
    }

    if (q.type === 'email') {
      const v = String(answers[q.id] ?? '').trim();
      if (!v) return !q.is_required;      // blank allowed only when optional
      return EMAIL_RE.test(v);            // non-blank must match the format
    }

    // standard_2026 contact opt-in screen: the No/Yes preference MUST be
    // chosen; selecting "Yes" requires a usable email OR mobile. "No" lets the
    // respondent continue without any contact info. Any entered contact value
    // is format-validated regardless of preference.
    if (q.type === 'contact_info' && isStd2026 && q.contact_optin) {
      const a = answers[q.id] || {};
      const pref = a.contact_pref;
      if (pref !== 'no' && pref !== 'yes') return false; // neither pre-selected
      const m = a.mobile;
      const mobNum = (m && typeof m === 'object') ? (m.number || '') : (typeof m === 'string' ? m : '');
      const email = String(a.email || '').trim();
      const validMobile = !!mobNum && MOBILE_NUMBER_RE.test(mobNum);
      const validEmail = !!email && EMAIL_RE.test(email);
      if (mobNum && !validMobile) return false;
      if (email && !validEmail) return false;
      if (pref === 'yes' && !validMobile && !validEmail) return false;
      return true;
    }

    if (q.type === 'attribute_ratings') {
      if (!q.is_required) return true;
      const attrs = Array.isArray(q.attributes) ? q.attributes : [];
      const a = answers[q.id];
      if (!a || typeof a !== 'object') return false;
      return attrs.every((at) => Number(a[at.id]) > 0);
    }

    if (q.type === 'number_input') {
      const raw = answers[q.id];
      const blank = raw === undefined || raw === null || String(raw).trim() === '';
      if (blank) return !q.is_required;           // blank only allowed when optional
      const n = Number(raw);
      if (Number.isNaN(n)) return false;
      const ns = q.number_settings || {};
      if (ns.min_value != null && n < ns.min_value) return false;  // e.g. must be > 0
      if (ns.max_value != null && n > ns.max_value) return false;
      return true;
    }

    if (q.type === 'matrix') {
      const a = answers[q.id];
      const itemsSource = q.items_source || 'fixed';
      const criteria = (Array.isArray(q.criteria) ? q.criteria : []).filter(Boolean);
      const answerRows = Array.isArray(a?.items) ? a.items : [];
      const fixedItems = (Array.isArray(q.items) ? q.items.filter(Boolean) : []);
      // Single-axis matrix (fixed list, no items, criteria defined) uses one
      // implicit unlabelled row keyed by ''.
      const expectedItems = itemsSource === 'freetext'
        ? answerRows.map(r => r.item).filter(Boolean)
        : (fixedItems.length > 0 ? fixedItems : (criteria.length > 0 ? [''] : []));
      if (expectedItems.length === 0) return !q.is_required;
      if (criteria.length === 0) return true;
      for (const it of expectedItems) {
        const row = answerRows.find(r => r.item === it);
        if (!row) return false;
        for (const c of criteria) {
          if (!row[c]) return false;
        }
      }
      // fall through to comment_required check below
    } else if (!q.is_required) {
      if (q.type === 'contact_info') {
        const a = answers[q.id] || {};
        const m = a.mobile;
        if (m && typeof m === 'object' && (m.number || '').length > 0 && !MOBILE_NUMBER_RE.test(m.number)) return false;
      }
    } else {
      const a = answers[q.id];
      if (a === undefined || a === null || a === '') return false;
      if (Array.isArray(a) && a.length === 0) return false;
      if (q.type === 'contact_info') {
        const cf = q.contact_fields || {};
        for (const [k, meta] of Object.entries(cf)) {
          if (!(meta.enabled && meta.required)) continue;
          if (k === 'mobile') {
            const m = (a || {}).mobile;
            if (!m || typeof m !== 'object') return false;
            if (!m.country_code || !MOBILE_NUMBER_RE.test(m.number || '')) return false;
          } else if (!(a || {})[k]) {
            return false;
          }
        }
        if (showConsent && !a.consent_given) return false;
      }
    }

    // Phase 1: when commentable + is_required + comment_required, the comment
    // textarea must contain at least one non-whitespace character before the
    // respondent can advance.
    if (q.commentable && q.is_required && q.comment_required) {
      const c = (comments[q.id] || '').trim();
      if (!c) return false;
    }
    return true;
  };

  const canProceed = useMemo(() => {
    if (!currentQuestion) return true;
    // Validate every question that's currently being rendered. In legacy
    // mode that's just the current step's question; in section-grouped mode
    // it's the section header plus all its child questions.
    for (const idx of visibleStepMembers) {
      if (!isQuestionAnswered(questions[idx])) return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, visibleStepMembers, answers, comments, followups, showConsent, hiddenIds]);

  const restartPreview = () => {
    setAnswers({});
    setComments({});
    setFollowups({});
    setStep(0);
    setDone(false);
  };

  const submit = async () => {
    if (isPreview) {
      setDone(true);
      return;
    }
    setSubmitting(true);
    try {
      // Exclude any conditionally-hidden questions from the submitted
      // payload — their answers (if any) were already cleared when they
      // became hidden, this is the belt-and-braces guarantee.
      const filteredAnswers = Object.fromEntries(
        Object.entries(answers).filter(([qid]) => !hiddenIds.has(qid))
      );
      // Upload any recorded voice blobs first so each voice answer carries an audio_url.
      const uploadedAnswers = {};
      for (const [qid, val] of Object.entries(filteredAnswers)) {
        if (val && typeof val === 'object' && !Array.isArray(val) && val.blob instanceof Blob) {
          const fd = new FormData();
          const filename = `voice_${qid}.webm`;
          fd.append('audio', val.blob, filename);
          fd.append('branch_id', branchId || '');
          fd.append('question_id', qid);
          let audioUrl = val.audio_url || '';
          try {
            const up = await fetch(`${API}/public/survey/upload-audio`, { method: 'POST', body: fd });
            if (up.ok) {
              const d = await up.json();
              audioUrl = d.audio_url || '';
            }
          } catch {
            // If the audio upload fails we still submit the transcript so feedback is not lost.
          }
          const { blob, ...rest } = val; // eslint-disable-line no-unused-vars
          uploadedAnswers[qid] = { ...rest, audio_url: audioUrl };
        } else {
          uploadedAnswers[qid] = val;
        }
      }

      // Trim leading/trailing whitespace on email answers before submission.
      for (const q of questions) {
        if (q.type === 'email' && typeof uploadedAnswers[q.id] === 'string') {
          uploadedAnswers[q.id] = uploadedAnswers[q.id].trim();
        }
      }

      // Attach commentable comments to their answers (as answer.comment).
      // Wraps primitives/arrays as { value, comment }; spreads onto object answers.
      for (const q of questions) {
        if (hiddenIds.has(q.id)) continue;
        if (!q.commentable || !COMMENTABLE_TYPES.has(q.type)) continue;
        const comment = (comments[q.id] || '').trim();
        if (!comment) continue;
        const existing = uploadedAnswers[q.id];
        if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
          uploadedAnswers[q.id] = { ...existing, comment };
        } else {
          uploadedAnswers[q.id] = { value: existing ?? null, comment };
        }
      }

      // Conditional follow-ups — only the selected option's value, sent
      // separately from the main answers (never merged into them).
      const followupPayload = {};
      for (const q of questions) {
        if (hiddenIds.has(q.id) || !(q.id in uploadedAnswers)) continue;
        const cfg = followupFor(q, answers[q.id]);
        const v = cfg ? followups[q.id]?.[cfg.option] : undefined;
        if (cfg && !isBlank(v)) followupPayload[q.id] = cfg.field_type === 'number' ? Number(v) : String(v).trim();
      }

      const res = await fetch(`${API}/public/survey/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, branch_id: branchId, answers: uploadedAnswers, ...(Object.keys(followupPayload).length ? { followups: followupPayload } : {}), ...(accessCredential ? { access: accessCredential } : {}) }),
      });
      if (res.ok) {
        clearDraft();
        setDone(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error((typeof err.detail === 'string' ? err.detail : err.detail?.message) || 'Submission failed');
      }
    } catch {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // B3: Post-submission redirect — fires once when entering the `done` state.
  // Conditions reference NPS/CSAT scores from the just-submitted answers.
  // Hook MUST be declared before any conditional early returns to satisfy
  // react-hooks/rules-of-hooks.
  useEffect(() => {
    if (!done || isPreview) return;
    const survey = bundle?.survey;
    let url = (survey?.redirect_url || '').trim();
    if (!url) return;
    // Heal legacy / bare-host URLs (e.g. "example.com/path") that would
    // otherwise be treated as relative paths and 404 on the survey domain.
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const condition = survey?.redirect_condition || 'always';
    const delay = Number(survey?.redirect_delay_seconds ?? 3);

    const npsQ = questions.find(q => q.type === 'nps');
    const csatQ = questions.find(q => q.type === 'csat' || q.type === 'star_rating' || q.type === 'rating_scale');
    const npsVal = npsQ ? Number(answers[npsQ.id]) : null;
    const csatVal = csatQ ? Number(answers[csatQ.id]) : null;

    const meets = (() => {
      if (condition === 'always') return true;
      if (condition === 'nps_9_10') return Number.isFinite(npsVal) && npsVal >= 9 && npsVal <= 10;
      if (condition === 'nps_7_8') return Number.isFinite(npsVal) && npsVal >= 7 && npsVal <= 8;
      if (condition === 'nps_0_6') return Number.isFinite(npsVal) && npsVal >= 0 && npsVal <= 6;
      if (condition === 'csat_4_5') return Number.isFinite(csatVal) && csatVal >= 4 && csatVal <= 5;
      if (condition === 'csat_1_3') return Number.isFinite(csatVal) && csatVal >= 1 && csatVal <= 3;
      return false;
    })();

    if (!meets) return;
    const t = setTimeout(() => { window.location.href = url; }, Math.max(0, delay) * 1000);
    return () => clearTimeout(t);
  }, [done, bundle, questions, answers, isPreview]);

  // ── Rendering ──

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5]" data-testid="survey-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[#64748B]" />
    </div>
  );

  if (errorMsg) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5] px-4" data-testid="survey-unavailable">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-sm">
        <AlertCircle className="w-12 h-12 mx-auto text-[#D62B27] mb-3" />
        <h1 className="text-lg font-bold text-[#0F172A]">{errorMsg}</h1>
        <p className="text-sm text-[#64748B] mt-2">Please contact the business for a new link, or try again later.</p>
      </div>
    </div>
  );

  if (done) {
    const npsQ = questions.find(q => q.type === 'nps');
    const npsScore = npsQ ? answers[npsQ.id] : null;
    // Prefer the thank_you block's own settings (Survey Builder → Thank You)
    // when present, falling back to the legacy survey-level thank_you_message.
    const thankYouBlock = questions.find(q => q.type === 'thank_you');
    const tys = thankYouBlock?.thank_you_settings || {};
    const thanks = tys.message || bundle?.survey?.thank_you_message || 'Thank you for your feedback';
    const reviewMode = tys.show_review_link;
    const reviewUrlRaw = tys.review_link_url || bundle?.survey?.google_review_url;
    // Heal bare-host URLs so the review button opens an absolute URL instead
    // of being treated as a relative path on the survey domain.
    const reviewUrl = reviewUrlRaw
      ? (/^https?:\/\//i.test(reviewUrlRaw.trim()) ? reviewUrlRaw.trim() : `https://${reviewUrlRaw.trim()}`)
      : '';
    const showReview = reviewUrl && (
      reviewMode === 'always'
        || (reviewMode === 'if_promoter' && npsScore != null && Number(npsScore) >= 9)
        // Legacy behaviour (no thank_you block configured): promoter only.
        || (!reviewMode && npsScore != null && Number(npsScore) >= 9)
    );
    return (
      <div className={`min-h-screen bg-[#F0F2F5] flex flex-col items-center justify-between py-10 px-4${isStd2026 ? ' std2026' : ''}`} data-testid="survey-thankyou">
        <div className="w-full max-w-md space-y-4">
          {isPreview && <PreviewBanner presentation={presentation} onRestart={restartPreview} />}
          {isStd2026 && (
            <div data-testid="survey-progress">
              <Progress value={100} className="h-1.5" />
              <p className="text-[10px] text-[#94A3B8] mt-1 text-center" data-testid="survey-progress-label">
                Step {stdTotalSteps} of {stdTotalSteps}
              </p>
            </div>
          )}
          <div className="w-full bg-white rounded-2xl p-6 shadow-sm text-center space-y-4">
            {logo && <img src={logo} alt={brandName} className="h-12 mx-auto" />}
            <CheckCircle className="w-14 h-14 mx-auto" style={{ color: secondary }} />
            <h1 className="text-xl font-bold text-[#0F172A]">{thanks}</h1>
            {tys.submessage && (
              <p className="text-sm text-[#475569]" data-testid="survey-thankyou-submessage">{tys.submessage}</p>
            )}
            {showReview && (
              <a href={reviewUrl} target="_blank" rel="noreferrer" data-testid="google-review-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: primary }}>
                <ExternalLink className="w-4 h-4" /> Leave a Google review
              </a>
            )}
          </div>
        </div>
        <div className="w-full max-w-md">
          <SurveyFooter footerSettings={footerSettings} primaryColor={primary} />
        </div>
      </div>
    );
  }

  const surveyHeader = (
    <header className="bg-white border-b border-[#E2E8F0] px-4 py-4 flex items-center gap-3" data-testid="survey-header">
      {logo && <img src={logo} alt={brandName} className="h-10" />}
      <div>
        <p className="text-xs text-[#94A3B8]">{bundle?.branch?.name}</p>
        <p className="text-sm font-semibold text-[#0F172A]">{brandName}</p>
      </div>
    </header>
  );

  if (accessOn && accessCredential === undefined) {
    const gateTotal = stdScreens.length + 2;
    const onGateGranted = (credential, r) => {
      if (entryKey && r?.branch) {
        setResolved({ branch_id: r.branch.branch_id, code: r.code });
        setBundle(b => ({ ...b, branch: r.branch, code: r.code }));
      }
      setAccessCredential(credential);
    };
    return (
      <SurveyAccessGate
        access={entryKey ? { ...bundle.access, required: true } : bundle.access}
        branchId={branchId}
        code={code}
        entry={entryKey ? { key: entryKey, settings: bundle.entry } : null}
        brandName={brandName}
        isStd2026={isStd2026}
        primary={primary}
        accessQuestion={(bundle?.questions || []).find(q => q.type === 'survey_access_code')}
        header={surveyHeader}
        progress={isStd2026 ? (
          <div className="px-4 pt-4">
            <div className="max-w-md md:max-w-2xl mx-auto" data-testid="survey-progress">
              <Progress value={Math.round(100 / gateTotal)} className="h-1.5" />
              <p className="text-[10px] text-[#94A3B8] mt-1" data-testid="survey-progress-label">Step 1 of {gateTotal}</p>
            </div>
          </div>
        ) : null}
        footer={<div className="px-4 pb-6 max-w-md md:max-w-2xl mx-auto w-full"><SurveyFooter footerSettings={footerSettings} primaryColor={primary} /></div>}
        onShown={() => setAccessScreenSeen(true)}
        onGranted={onGateGranted}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-[#F0F2F5] flex flex-col${isStd2026 ? ' std2026' : ''}`} data-testid="survey-form" data-presentation={presentation}>
      {isPreview && <PreviewBanner presentation={presentation} onRestart={restartPreview} />}
      {/* Header */}
      {surveyHeader}

      {/* Phase 1: Resume saved draft banner — appears once on load when a
          previously saved draft is found in localStorage. */}
      {draftBanner && (
        <div className="px-4 pt-3" data-testid="resume-draft-banner">
          <div className="max-w-md md:max-w-2xl mx-auto bg-[#FEF7EC] border border-[#F4D484] rounded-lg px-3 py-2 flex items-center justify-between gap-2">
            <p className="text-xs text-[#92400E] flex-1">
              You have a saved draft from {new Date(draftBanner.savedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="ghost" onClick={discardDraft} className="text-[11px] h-7 px-2 text-[#92400E]" data-testid="discard-draft-btn">
                Start over
              </Button>
              <Button size="sm" onClick={resumeDraft} className="text-[11px] h-7 px-3 text-white" style={{ backgroundColor: primary }} data-testid="resume-draft-btn">
                Resume
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="px-4 pt-4">
        <div className="max-w-md md:max-w-2xl mx-auto" data-testid="survey-progress">
          <Progress value={isStd2026 ? stdProgressPct : progress} className="h-1.5" />
          <p className="text-[10px] text-[#94A3B8] mt-1" data-testid="survey-progress-label">
            {isStd2026
              ? `Step ${stdCurrentStep} of ${stdTotalSteps}`
              : (isAnswerableCurrent
                ? `Question ${currentAnswerablePos} of ${visibleCount}`
                : `${visibleCount} ${visibleCount === 1 ? 'question' : 'questions'}`)}
          </p>
        </div>
      </div>

      {/* Question */}
      <main className="flex-1 px-4 py-6">
        <div className={`max-w-md md:max-w-2xl mx-auto bg-white rounded-2xl p-5 md:p-7 shadow-sm${isStd2026 ? ' std2026-container' : ''}`}>
          {currentQuestion ? (
            // Section-grouped mode: render the section header + every child
            // question in the same scrollable card. Falls back to a single
            // question render in legacy mode (visibleStepMembers === [step]).
            visibleStepMembers.length > 1 ? (
              <div className="space-y-6" data-testid="survey-section-group">
                {visibleStepMembers.map((idx, i) => {
                  const q = questions[idx];
                  if (!q) return null;
                  return (
                    <div key={q.id} className={i > 0 ? 'pt-5 border-t border-[#F1F5F9]' : ''}>
                      <QuestionRenderer
                        question={effectiveQ(q)}
                        answer={answers[q.id]}
                        onChange={(v) => setAnswer(q.id, v)}
                        primaryColor={primary}
                        privacyUrl={privacyUrl}
                        termsUrl={footerSettings.custom_footer_text?.startsWith('http') ? footerSettings.custom_footer_text : ''}
                        brandName={brandName}
                        onVoiceBusyChange={setVoiceBusy}
                        onUploadingChange={setUploadingFor}
                        step={idx}
                        showConsent={showConsent}
                        defaultDialCode={defaultDialCode}
                        sessionKey={code}
                        branchId={branchId}
                        onAdvance={() => {}}
                        questionNumber={numberMap[q.id] || null}
                        presentation={presentation}
                        isPreview={isPreview}
                        followupValues={followups[q.id] || {}}
                        onFollowupChange={(opt, v) => setFollowup(q.id, opt, v)}
                      />
                      {q.commentable && COMMENTABLE_TYPES.has(q.type) && (
                        <div className="mt-4">
                          <Textarea
                            rows={3}
                            value={comments[q.id] || ''}
                            onChange={(e) => setComments(prev => ({ ...prev, [q.id]: e.target.value }))}
                            placeholder={q.comment_required ? "Comment required..." : "Please give details..."}
                            data-testid={`answer-comment-${q.id}`}
                            style={{ borderColor: q.comment_required && q.is_required && !(comments[q.id] || '').trim() ? '#D62B27' : '#E2E8F0' }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <QuestionRenderer
                  key={currentQuestion.id}
                  question={effectiveQ(currentQuestion)}
                  answer={answers[currentQuestion.id]}
                  onChange={(v) => setAnswer(currentQuestion.id, v)}
                  primaryColor={primary}
                  privacyUrl={privacyUrl}
                  termsUrl={footerSettings.custom_footer_text?.startsWith('http') ? footerSettings.custom_footer_text : ''}
                  brandName={brandName}
                  onVoiceBusyChange={setVoiceBusy}
                  onUploadingChange={setUploadingFor}
                  step={step}
                  showConsent={showConsent}
                  defaultDialCode={defaultDialCode}
                  sessionKey={code}
                  branchId={branchId}
                  onAdvance={() => setStep(s => nextVisibleStep(s))}
                  questionNumber={numberMap[currentQuestion.id] || null}
                  presentation={presentation}
                  isPreview={isPreview}
                  followupValues={followups[currentQuestion.id] || {}}
                  onFollowupChange={(opt, v) => setFollowup(currentQuestion.id, opt, v)}
                />
                {currentQuestion.commentable && COMMENTABLE_TYPES.has(currentQuestion.type) && (
                  <div className="mt-4">
                    <Textarea
                      rows={3}
                      value={comments[currentQuestion.id] || ''}
                      onChange={(e) => setComments(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                      placeholder={currentQuestion.comment_required ? "Comment required..." : "Please give details..."}
                      data-testid={`answer-comment-${currentQuestion.id}`}
                      style={{
                        borderColor: currentQuestion.comment_required && currentQuestion.is_required && !(comments[currentQuestion.id] || '').trim()
                          ? '#D62B27' : '#E2E8F0',
                      }}
                    />
                  </div>
                )}
              </>
            )
          ) : (
            <p className="text-center text-[#94A3B8]">No questions configured.</p>
          )}
        </div>

        {/* Nav — hidden on the Welcome screen which has its own Start button. */}
        {currentQuestion?.type !== 'welcome' && (
          <>
            <div className="max-w-md md:max-w-2xl mx-auto mt-4 flex items-center justify-between gap-2">
              <Button variant="outline" onClick={() => setStep(s => prevVisibleStep(s))} disabled={step === 0 || voiceBusy} data-testid="survey-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-2">
                {draftKey && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={saveDraft}
                    disabled={voiceBusy}
                    className="text-xs text-[#475569] hover:text-[#0F172A]"
                    data-testid="survey-save-draft-btn"
                  >
                    Save draft
                  </Button>
                )}
                {!isLast ? (
                  <Button onClick={() => setStep(s => nextVisibleStep(s))} disabled={!canProceed || voiceBusy || anyUploading} style={{ backgroundColor: primary }} className="text-white" data-testid="survey-next-btn">
                    {anyUploading ? 'Uploading…' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={submit} disabled={!canProceed || submitting || voiceBusy || anyUploading} style={{ backgroundColor: primary }} className="text-white" data-testid="survey-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Submit
                  </Button>
                )}
              </div>
            </div>
            {draftSavedToast && (
              <p className="max-w-md md:max-w-2xl mx-auto mt-2 text-right text-[11px] text-[#22C55E]" data-testid="draft-saved-toast">
                ✓ Draft saved {new Date(draftSavedToast).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </>
        )}
      </main>

      <div className="px-4">
        <div className="max-w-md md:max-w-2xl mx-auto">
          {showNpsTrademark && (
            <p
              className="text-center text-[11px] text-[#94A3B8] leading-snug mt-2"
              data-testid="nps-trademark-footer"
            >
              Net Promoter&reg; Score is a registered trademark of Bain &amp; Company, Satmetrix Systems, and Fred Reichheld.
            </p>
          )}
          <SurveyFooter footerSettings={footerSettings} primaryColor={primary} />
        </div>
      </div>
    </div>
  );
}
